/* tslint:disable */
/* eslint-disable */
/**
 * Scrivio Contracts API
 * API for managing contracts between organizations and health insurers (verzekeraars). Contracts determine pricing for declarations (declaraties) including negotiated percentages of NZa tariffs and optional volume ceilings (plafonds).  A contract is with a single insurer (`insurerId`) or with a whole insurer concern (`concernId`) — a.s.r. is three labels, Achmea five, and those agreements are negotiated with the concern. Exactly one of the two is set. A consult is still declared to the **label\'s** UZOVI; the concern only says which agreement prices the line, and a label that has a contract of its own is priced by that one.  Multiple contracts per insurer are allowed, each covering different settings (behandelsettings). Each setting can only be covered by one active contract per insurer at a time.  Contracts are created in ACTIVE status. When creating a new contract that covers settings already covered by another active contract for the same insurer, a 409 Conflict error is returned.  All operations are scoped to the authenticated user\'s organization. 
 *
 * The version of the OpenAPI document: 1.0.0
 * 
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */


import type { Configuration } from './configuration';
import type { AxiosPromise, AxiosInstance, RawAxiosRequestConfig } from 'axios';
import globalAxios from 'axios';
// Some imports not used depending on template conditions
// @ts-ignore
import { DUMMY_BASE_URL, assertParamExists, setApiKeyToObject, setBasicAuthToObject, setBearerAuthToObject, setOAuthToObject, setSearchParams, serializeDataIfNeeded, toPathString, createRequestFunction } from './common';
import type { RequestArgs } from './base';
// @ts-ignore
import { BASE_PATH, COLLECTION_FORMATS, BaseAPI, RequiredError, operationServerMap } from './base';

/**
 * Type of healthcare activity: - OTHER: none of the four - a toeslag or an overige prestatie 
 */

export const ActivityType = {
    Diagnostics: 'DIAGNOSTICS',
    Treatment: 'TREATMENT',
    Crisis: 'CRISIS',
    Residential: 'RESIDENTIAL',
    Other: 'OTHER'
} as const;

export type ActivityType = typeof ActivityType[keyof typeof ActivityType];


/**
 * Billing type based on contract status: - CONTRACTED: Has active contract, use negotiated rates - NON_CONTRACTED: No contract, priced and declared at the full NZa maximum (100%) 
 */

export const BillingType = {
    Contracted: 'CONTRACTED',
    NonContracted: 'NON_CONTRACTED'
} as const;

export type BillingType = typeof BillingType[keyof typeof BillingType];


/**
 * Care intensity level for residential stays (verblijfsdagen) in the ZPM: - A: Lichte verzorgingsgraad (light care) - B: Matige verzorgingsgraad - C: Gemiddelde verzorgingsgraad - D: Zware verzorgingsgraad - E: Intensieve verzorgingsgraad (intensive care) 
 */

export const CareIntensity = {
    A: 'A',
    B: 'B',
    C: 'C',
    D: 'D',
    E: 'E'
} as const;

export type CareIntensity = typeof CareIntensity[keyof typeof CareIntensity];


/**
 * Information about a setting that conflicts with an existing contract
 */
export interface ConflictingSetting {
    /**
     * ID of the conflicting setting
     */
    'settingId': string;
    /**
     * Code of the conflicting setting
     */
    'settingCode': string;
    /**
     * Name of the conflicting setting
     */
    'settingName': string;
    /**
     * ID of the existing contract that already covers this setting
     */
    'existingContractId': string;
}
/**
 * Type of consult for service codes in the Zorgprestatiemodel (ZPM). One of the 4 dimensions that define a service code: - DIAGNOSTIEK: Diagnostic session - BEHANDELING: Treatment session - VERBLIJF: Residential stay (verblijfsdagen) - uses care intensity for billing code lookup 
 */

export const ConsultType = {
    Diagnostiek: 'DIAGNOSTIEK',
    Behandeling: 'BEHANDELING',
    Verblijf: 'VERBLIJF'
} as const;

export type ConsultType = typeof ConsultType[keyof typeof ConsultType];


export interface ContractListResponse {
    /**
     * List of contracts for current page
     */
    'content': Array<ContractResponse>;
    /**
     * Current page number (0-indexed)
     */
    'page': number;
    /**
     * Number of items per page
     */
    'size': number;
    /**
     * Total number of contracts
     */
    'totalElements': number;
    /**
     * Total number of pages
     */
    'totalPages': number;
}
/**
 * One priced scope of a contract. The scope is stated explicitly rather than inferred from which id happens to be filled: a request that means \"ARTS_BIG14 at 95 %\" and loses its `professionCode` would otherwise silently become the contract\'s default, and price every consult at 95 %. 
 */
export interface ContractPriceRuleRequest {
    'scope': ContractPriceScope;
    /**
     * Prestatiecode this rule prices. Required for `SERVICE_CODE`, rejected for every other scope.
     */
    'serviceCodeId'?: string | null;
    /**
     * Beroep this rule prices, matched against the beroepscode of the practitioner who delivered the consult (`users.profession_code`) — not against what the resolved prestatiecode says about itself (#4026).  One of `ARTS_BIG3`, `ARTS_BIG14`, `GZ_PSYCH`, `KLIN_PSYCH`, `PSYCHOTHER`, `VERPL_SPEC`, `OVERIG`. Required for `PROFESSION`, rejected for every other scope. 
     */
    'professionCode'?: string | null;
    /**
     * Percentage of the NZa maximum this scope is agreed at.
     */
    'percentageOfNza'?: number | null;
    /**
     * An agreed amount in euro, which wins over `percentageOfNza` when both are set.
     */
    'fixedTariffEuro'?: number | null;
    /**
     * Notes for this rule
     */
    'notes'?: string | null;
}


export interface ContractPriceRuleResponse {
    /**
     * Price rule unique identifier
     */
    'id': string;
    /**
     * Contract this rule belongs to
     */
    'contractId': string;
    'scope': ContractPriceScope;
    /**
     * Prestatiecode this rule prices (`SERVICE_CODE` scope only)
     */
    'serviceCodeId'?: string | null;
    /**
     * Beroep this rule prices (`PROFESSION` scope only)
     */
    'professionCode'?: string | null;
    /**
     * Behandelsetting this rule prices (`SETTING` scope only). These rules are derived from the contract\'s behandelsettings and are read-only here. 
     */
    'settingId'?: string | null;
    /**
     * Percentage of the NZa maximum this scope is agreed at
     */
    'percentageOfNza'?: number | null;
    /**
     * Agreed amount in euro, which wins over the percentage when both are set
     */
    'fixedTariffEuro'?: number | null;
    /**
     * Notes
     */
    'notes'?: string | null;
    /**
     * Timestamp when the rule was created
     */
    'createdAt': number;
    /**
     * Timestamp when the rule was last updated
     */
    'updatedAt': number;
}


/**
 * The scope a contract price rule names, **most specific first**. This order is the precedence a line is priced by: prestatiecode, then beroep, then behandelsetting, then the contract\'s own default.  \"Uncontracted\" is deliberately not a member: it is the absence of a matching rule, and it prices at the full NZa maximum (#3822). 
 */

export const ContractPriceScope = {
    ServiceCode: 'SERVICE_CODE',
    Profession: 'PROFESSION',
    Setting: 'SETTING',
    ContractDefault: 'CONTRACT_DEFAULT'
} as const;

export type ContractPriceScope = typeof ContractPriceScope[keyof typeof ContractPriceScope];


export interface ContractResponse {
    /**
     * Contract unique identifier
     */
    'id': string;
    /**
     * Organization this contract belongs to
     */
    'organizationId': string;
    /**
     * Insurer (verzekeraar) ID when this contract is with a single label. Null when it covers a whole concern — then `concernId` is set instead. 
     */
    'insurerId'?: string | null;
    /**
     * The party this contract is with: the insurer\'s name, or the concern\'s when it covers every label of one. 
     */
    'insurerName': string;
    /**
     * Insurer UZOVI code. Absent on a concern-scoped contract — a concern has no UZOVI of its own.
     */
    'insurerUzoviCode'?: string;
    /**
     * Insurer concern (koepel) ID when this contract covers every label of one. Null when it is with a single insurer. Exactly one of `insurerId` and `concernId` is set. 
     */
    'concernId'?: string | null;
    /**
     * Name of the concern this contract covers (when `concernId` is set)
     */
    'concernName'?: string | null;
    /**
     * How many insurer labels the covered concern holds (when `concernId` is set)
     */
    'concernInsurerCount'?: number | null;
    /**
     * Optional location ID for AGB code. When null, Organization.agbCode is used as fallback.
     */
    'locationId'?: string | null;
    /**
     * Location name (when locationId is set)
     */
    'locationName'?: string | null;
    /**
     * Insurer parent organization (koepelorganisatie)
     */
    'parentOrganization'?: string | null;
    /**
     * Contract reference number
     */
    'contractNumber'?: string | null;
    'status': ContractStatus;
    /**
     * Date when this contract takes effect (takes over from previous)
     */
    'effectiveDate': string;
    /**
     * Contract start date
     */
    'validFrom': string;
    /**
     * Contract end date
     */
    'validUntil'?: string | null;
    /**
     * Optional ceiling/plafond amount in euros
     */
    'ceilingEuro'?: number | null;
    /**
     * Ceiling type (yearly, total, etc.)
     */
    'ceilingType'?: string | null;
    /**
     * Amount of ceiling used so far
     */
    'ceilingUsedEuro'?: number;
    /**
     * Percentage of ceiling used (0-100)
     */
    'ceilingPercentage'?: number;
    /**
     * Contract notes
     */
    'notes'?: string | null;
    'sendMethod'?: ContractSendMethod;
    /**
     * Number of service codes with custom percentages
     */
    'serviceCount'?: number;
    /**
     * Settings (behandelsettings) covered by this contract with their pricing
     */
    'settings'?: Array<ContractSettingSummary>;
    /**
     * Timestamp when contract was created
     */
    'createdAt': number;
    /**
     * Timestamp when contract was last updated
     */
    'updatedAt': number;
}


/**
 * Method for submitting invoices/claims for a contract: - PAPER: Manual invoice generation (PDF), sent via post/email to clients or factoring companies - VECOZO: Automated submission via GDS801 XML to the Vecozo healthcare data exchange network  This enum is aligned with BillingMethod at the organization level. 
 */

export const ContractSendMethod = {
    Paper: 'PAPER',
    Vecozo: 'VECOZO'
} as const;

export type ContractSendMethod = typeof ContractSendMethod[keyof typeof ContractSendMethod];


/**
 * How much of one soort prestatie this contract prices, over the filtered set.  This is what answers \"did my tariff sheet land?\" — the question filtering can only answer six clicks at a time. `agreedCount` counts the codes this contract names a `contract_price_rule` for, whichever way that rule prices; `totalCount` counts the codes in the filtered set, agreed or not. 
 */
export interface ContractServiceCodeCoverageResponse {
    'serviceType': ServiceType;
    /**
     * Codes of this soort prestatie the contract names a price rule for
     */
    'agreedCount': number;
    /**
     * Codes of this soort prestatie in the filtered set
     */
    'totalCount': number;
}


export interface ContractServiceCodeTariffListResponse {
    /**
     * Prestatiecodes for the current page
     */
    'content': Array<ContractServiceCodeTariffResponse>;
    /**
     * Agreed against total per soort prestatie, over the whole filtered set rather than the page — every filter applies except `agreedOnly`, which selects on the very thing being measured. A soort prestatie the filter leaves nothing of is absent rather than reported as zero. 
     */
    'coverage': Array<ContractServiceCodeCoverageResponse>;
    /**
     * Current page number (0-indexed)
     */
    'page': number;
    /**
     * Number of items per page
     */
    'size': number;
    /**
     * Total number of prestatiecodes matching the filter
     */
    'totalElements': number;
    /**
     * Total number of pages
     */
    'totalPages': number;
}
/**
 * One prestatiecode and what this contract agrees for it.  `priceRuleId` is what says whether the contract names the code at all. It is `null` when nothing is agreed and the line falls through to beroep, behandelsetting and the contract default; when it is set, exactly one of `agreedTariffEuro` and `agreedPercentageOfNza` carries the agreement. Do not read an absent `agreedTariffEuro` as \"not agreed\" — a code priced as a percentage has none, and `0.00` means the contract agrees the code at nothing. The three are deliberately not the same fact. 
 */
export interface ContractServiceCodeTariffResponse {
    /**
     * Prestatiecode UUID
     */
    'serviceCodeId': string;
    /**
     * Prestatiecode (e.g. CO0432)
     */
    'code': string;
    /**
     * What the prestatiecode covers
     */
    'description': string;
    /**
     * The `contract_price_rule` this agreement is, to address it on `/api/v1/contracts/{contractId}/price-rules/{priceRuleId}` — the one write path for a price rule. `null` exactly when this contract names no rule for the code, and the only field that answers that question. 
     */
    'priceRuleId'?: string | null;
    /**
     * The amount this contract agrees for the code — `null` when it agrees none *and* when it agrees a percentage instead. Always sent, never omitted: a consumer that only checks for absence would otherwise read \"no agreement\" and \"agreed at € 0,00\" the same way. How a.s.r. and Zorg en Zekerheid price. 
     */
    'agreedTariffEuro'?: number | null;
    /**
     * The percentage of the NZa maximum this contract agrees for the code, when the rule is priced that way rather than as an amount. `null` otherwise. A price rule carries one of the two, never neither. 
     */
    'agreedPercentageOfNza'?: number | null;
}
export interface ContractSettingRequest {
    /**
     * Setting (behandelsetting) ID
     */
    'settingId': string;
    /**
     * Percentage of NZa tariff (e.g., 95.0)
     */
    'percentageOfNza': number;
    /**
     * Optional fixed tariff override
     */
    'fixedTariffEuro'?: number | null;
    /**
     * Notes for this setting
     */
    'notes'?: string | null;
}
export interface ContractSettingResponse {
    /**
     * Contract setting unique identifier
     */
    'id': string;
    /**
     * Contract ID
     */
    'contractId': string;
    /**
     * Setting (behandelsetting) ID
     */
    'settingId': string;
    /**
     * Setting code (e.g., \"MONO\", \"MULTI\")
     */
    'settingCode': string;
    /**
     * Setting name (e.g., \"Monodisciplinair\", \"Multidisciplinair\")
     */
    'settingName': string;
    'serviceSetting'?: ServiceSetting;
    /**
     * Percentage of NZa tariff
     */
    'percentageOfNza': number;
    /**
     * Fixed tariff override (if set)
     */
    'fixedTariffEuro'?: number | null;
    /**
     * Notes
     */
    'notes'?: string | null;
    /**
     * Timestamp when contract setting was created
     */
    'createdAt': number;
    /**
     * Timestamp when contract setting was last updated
     */
    'updatedAt': number;
}


/**
 * Summary of a setting covered by a contract, used in ContractResponse
 */
export interface ContractSettingSummary {
    /**
     * Setting (behandelsetting) ID
     */
    'settingId': string;
    /**
     * Setting code (e.g., \"MONO\", \"MULTI\")
     */
    'settingCode': string;
    /**
     * Setting name (e.g., \"Monodisciplinair\", \"Multidisciplinair\")
     */
    'settingName': string;
    /**
     * Percentage of NZa tariff for this setting
     */
    'percentageOfNza': number;
}
/**
 * Contract status: - ACTIVE: Currently valid and in use - EXPIRED: Past end date or superseded by a new contract 
 */

export const ContractStatus = {
    Active: 'ACTIVE',
    Expired: 'EXPIRED'
} as const;

export type ContractStatus = typeof ContractStatus[keyof typeof ContractStatus];


export interface ContractSummaryResponse {
    /**
     * Number of active contracts
     */
    'activeContracts': number;
    /**
     * Contracts expiring in the next 30 days
     */
    'expiringWithin30Days': Array<ContractResponse>;
    /**
     * Contracts approaching their ceiling
     */
    'plafondWarnings': Array<PlafondWarning>;
}
/**
 * Exactly one of `insurerId` and `concernId` must be set: a contract is with one label, or with every label of one concern. OpenAPI 3.0 cannot express that, so it is stated here and enforced by the API — sending both, or neither, is a 400. 
 */
export interface CreateContractRequest {
    /**
     * Insurer (verzekeraar) ID. Omit when contracting with a whole concern.
     */
    'insurerId'?: string | null;
    /**
     * Insurer concern (koepel) ID — one agreement covering every label of the concern. Omit when contracting with a single insurer. 
     */
    'concernId'?: string | null;
    /**
     * Optional location ID for AGB code. When null, Organization.agbCode is used as fallback.
     */
    'locationId'?: string | null;
    /**
     * Contract reference number
     */
    'contractNumber'?: string | null;
    /**
     * Date when this contract takes effect (any existing active contract for this insurer will be auto-expired the day before)
     */
    'effectiveDate': string;
    /**
     * Contract start date
     */
    'validFrom': string;
    /**
     * Contract end date
     */
    'validUntil'?: string | null;
    /**
     * Optional ceiling/plafond amount in euros
     */
    'ceilingEuro'?: number | null;
    /**
     * Ceiling type (yearly, total, etc.)
     */
    'ceilingType'?: string | null;
    /**
     * Contract notes
     */
    'notes'?: string | null;
    'sendMethod'?: ContractSendMethod;
    /**
     * Optional settings to add to the contract during creation
     */
    'settings'?: Array<ContractSettingRequest>;
}


/**
 * A gemeente contract with its gemeenten and products. Refused with a 400 when the organisation has not enabled `financingRegime`. 
 */
export interface CreateMunicipalContractRequest {
    'financingRegime': MunicipalFinancingRegime;
    /**
     * The gemeente\'s or region\'s contract reference
     */
    'contractNumber'?: string | null;
    /**
     * The jeugdregio the gemeenten were filled from (`GET /api/v1/jeugdregios`), recorded for display only — `gemeentecodes` is what the contract covers, and need not equal the region\'s gemeenten. Omit or null when the gemeenten were picked one by one. 
     */
    'jeugdregioCode'?: string | null;
    'validFrom': string;
    /**
     * Inclusive end date; omit for an open-ended contract
     */
    'validUntil'?: string | null;
    'notes'?: string | null;
    /**
     * The CBS gemeentecodes this contract covers — an explicit set, each validated against the gemeenten list. A gemeente may appear on several of the practice\'s contracts. 
     */
    'gemeentecodes': Array<string>;
    /**
     * The products the contract holds; may be empty and added later
     */
    'products': Array<MunicipalContractProductRequest>;
}


/**
 * A gemeente as CBS lists it on the date asked for
 */
export interface GemeenteResponse {
    /**
     * The 4-digit CBS gemeentecode
     */
    'gemeentecode': string;
    'gemeentenaam': string;
}
/**
 * iJw `LDT_RedenBeeindiging`, the reason on a JW307: OVERLIJDEN (02), TIJDELIJK_BEEINDIGD (20), VOLGENS_PLAN (31), VOORTIJDIG_DOOR_CLIENT (32), VOORTIJDIG_DOOR_AANBIEDER (33), VOORTIJDIG_IN_OVEREENSTEMMING (34), VOORTIJDIG_EXTERNE_OMSTANDIGHEDEN (35), GEINITIEERD_DOOR_GEMEENTE (36), WIJZIGINGSVERZOEK (37), OVERSTAP_ANDERE_AANBIEDER (38), UITSTROOM_ANDER_DOMEIN (39) 
 */

export const IjwRedenBeeindiging = {
    Overlijden: 'OVERLIJDEN',
    TijdelijkBeeindigd: 'TIJDELIJK_BEEINDIGD',
    VolgensPlan: 'VOLGENS_PLAN',
    VoortijdigDoorClient: 'VOORTIJDIG_DOOR_CLIENT',
    VoortijdigDoorAanbieder: 'VOORTIJDIG_DOOR_AANBIEDER',
    VoortijdigInOvereenstemming: 'VOORTIJDIG_IN_OVEREENSTEMMING',
    VoortijdigExterneOmstandigheden: 'VOORTIJDIG_EXTERNE_OMSTANDIGHEDEN',
    GeinitieerdDoorGemeente: 'GEINITIEERD_DOOR_GEMEENTE',
    Wijzigingsverzoek: 'WIJZIGINGSVERZOEK',
    OverstapAndereAanbieder: 'OVERSTAP_ANDERE_AANBIEDER',
    UitstroomAnderDomein: 'UITSTROOM_ANDER_DOMEIN'
} as const;

export type IjwRedenBeeindiging = typeof IjwRedenBeeindiging[keyof typeof IjwRedenBeeindiging];


/**
 * A zorgverzekeraar concern (koepel) and the labels it holds — what a contract naming `concernId` covers. 
 */
export interface InsurerConcernResponse {
    /**
     * Concern unique identifier
     */
    'id': string;
    /**
     * Concern name (koepelorganisatie), e.g. \"a.s.r.\" or \"Achmea\"
     */
    'name': string;
    /**
     * The insurer labels this concern holds, alphabetically
     */
    'insurers': Array<InsurerResponse>;
}
export interface InsurerResponse {
    /**
     * Insurer unique identifier
     */
    'id': string;
    /**
     * 4-digit UZOVI code
     */
    'uzoviCode': string;
    /**
     * Insurer name
     */
    'name': string;
    /**
     * Parent organization (koepelorganisatie)
     */
    'parentOrganization'?: string | null;
    /**
     * The insurer concern this label belongs to — the id a contract covering the whole koepel names. Derived from `parentOrganization`; null when the insurer names none. 
     */
    'concernId'?: string | null;
    /**
     * Street address
     */
    'addressStreet'?: string | null;
    /**
     * Postal code
     */
    'addressPostalCode'?: string | null;
    /**
     * City
     */
    'addressCity'?: string | null;
    /**
     * Phone number
     */
    'phone'?: string | null;
    /**
     * Email address
     */
    'email'?: string | null;
    /**
     * Website URL
     */
    'website'?: string | null;
    /**
     * Whether insurer is active
     */
    'isActive': boolean;
    /**
     * Whether this insurer should be shown in pickers. Set to false for non-consumer-facing entries such as payment-processing entities (e.g. \"Centrale Verwerkingseenheid CZ\"). Frontends should filter on this flag when listing selectable insurers. 
     */
    'isSelectable': boolean;
}
/**
 * A jeugdregio and the gemeenten it held on the date asked for
 */
export interface JeugdregioResponse {
    /**
     * The CBS jeugdregio code
     */
    'code': string;
    'naam': string;
    /**
     * Ordered by name
     */
    'gemeenten': Array<GemeenteResponse>;
}
/**
 * The message on which a lump-sum share becomes payable: START (JW305, start zorg) or STOP (JW307, stop zorg). 
 */

export const LumpSumTrigger = {
    Start: 'START',
    Stop: 'STOP'
} as const;

export type LumpSumTrigger = typeof LumpSumTrigger[keyof typeof LumpSumTrigger];


/**
 * The maximum budget the contract allows for one productcategorie over one period — the ceiling an aspecifieke toewijzing (a categorie and a budget, no product code) may request. Not a product: it is never declared. Refused with a field-level 400 when: - `productCategorie` is not in `LDT_ProductCategorie` of the contract\'s regime; - `geldigTot` lies before `geldigVanaf`; - its period overlaps another budget of the same categorie on this contract. 
 */
export interface MunicipalCategoryBudgetRequest {
    /**
     * `LDT_ProductCategorie`, e.g. 45 (jeugdhulp ambulant) or 54 (jeugd-ggz)
     */
    'productCategorie': string;
    'geldigVanaf': string;
    /**
     * Inclusive; omit while the budget is open-ended
     */
    'geldigTot'?: string | null;
    /**
     * The maximum budget a toewijzing in this categorie may request, in eurocent
     */
    'budgetEurocent': number;
}
export interface MunicipalCategoryBudgetResponse {
    'id': string;
    'contractId': string;
    'productCategorie': string;
    'geldigVanaf': string;
    'geldigTot'?: string | null;
    'budgetEurocent': number;
}
export interface MunicipalContractGemeente {
    'gemeentecode': string;
    'gemeentenaam': string;
}
/**
 * The jeugdregio a gemeente contract\'s gemeenten were filled from — for display only; null when they were picked one by one 
 */
export interface MunicipalContractJeugdregio {
    'code': string;
    'naam': string;
}
/**
 * One product the contract holds, with its tariffs and shares, validated as a whole. Refused with a field-level 400 when: - `productCategorie` is not in `LDT_ProductCategorie` of the contract\'s regime; - `productCode` is blank, or the contract already holds this categorie and code; - two of its `tarieven` overlap; - a GEEN product carries a tariff; - a tariff\'s `tariefEenheid` cannot convert to the product\'s `eenheid`; - a LUMP_SUM product\'s `aandelen` do not sum to 100, or another product carries any; - a START share names stopredenen.  A code the national product list knows under a different categorie is accepted: codes are not unique nationally, and the response reports it in `nationalProductCategorie`. 
 */
export interface MunicipalContractProductRequest {
    /**
     * `LDT_ProductCategorie`, e.g. 45 (jeugdhulp ambulant) or 54 (jeugd-ggz)
     */
    'productCategorie': string;
    /**
     * The contract\'s own product code. Not unique nationally.
     */
    'productCode': string;
    /**
     * Free description, e.g. the region\'s label `50AA++`
     */
    'omschrijving'?: string | null;
    'uitvoeringsvariant': Uitvoeringsvariant;
    'eenheid': ToewijzingEenheid;
    'declaratiemethode': MunicipalDeclaratiemethode;
    /**
     * Whether indirect time counts towards the delivered volume
     */
    'indirecteTijdTelt': boolean;
    /**
     * Whether travel time counts as indirect time
     */
    'reistijdTeltAlsIndirect': boolean;
    /**
     * The beroepen allowed to deliver this product; empty means anyone
     */
    'toegestaneBeroepen': Array<ProfessionCode>;
    'maxOmvang'?: MunicipalProductMaxOmvang | null;
    /**
     * Maximum duration in months; used to prefill and warn only
     */
    'maxDuurMaanden'?: number | null;
    /**
     * Whether the tariff in force on the toewijzing\'s start date finances the whole trajectory, rather than each period\'s own. 
     */
    'tariefVastOpToewijzingsdatum': boolean;
    /**
     * The dated price list. A change of price is a new entry; two entries never overlap. Must be empty for a GEEN product. 
     */
    'tarieven': Array<MunicipalProductTarief>;
    /**
     * The shares of a LUMP_SUM product, summing to 100. Empty for every other product.
     */
    'aandelen': Array<MunicipalProductAandeel>;
}


export interface MunicipalContractProductResponse {
    'id': string;
    'contractId': string;
    'productCategorie': string;
    'productCode': string;
    'omschrijving'?: string | null;
    'uitvoeringsvariant': Uitvoeringsvariant;
    'eenheid': ToewijzingEenheid;
    'declaratiemethode': MunicipalDeclaratiemethode;
    'indirecteTijdTelt': boolean;
    'reistijdTeltAlsIndirect': boolean;
    'toegestaneBeroepen': Array<ProfessionCode>;
    'maxOmvang'?: MunicipalProductMaxOmvang | null;
    'maxDuurMaanden'?: number | null;
    'tariefVastOpToewijzingsdatum': boolean;
    /**
     * The categorie the national product list knows this code under, when that differs from `productCategorie` — a warning, not an error: codes are not unique nationally. Null when the list does not know the code or agrees. 
     */
    'nationalProductCategorie'?: string | null;
    'tarieven': Array<MunicipalProductTariefResponse>;
    'aandelen': Array<MunicipalProductAandeel>;
}


export interface MunicipalContractResponse {
    'id': string;
    'financingRegime': MunicipalFinancingRegime;
    'contractNumber'?: string | null;
    'status': ContractStatus;
    'validFrom': string;
    'validUntil'?: string | null;
    'notes'?: string | null;
    'jeugdregio'?: MunicipalContractJeugdregio | null;
    'gemeenten': Array<MunicipalContractGemeente>;
    'products': Array<MunicipalContractProductResponse>;
    /**
     * The budget per productcategorie, by categorie and then period
     */
    'categoryBudgets': Array<MunicipalCategoryBudgetResponse>;
    'createdAt': number;
    'updatedAt': number;
}


/**
 * How a gemeente contract product is declared — one value per verified shape: - VOLUME: delivered minutes or hours x tariff, monthly - PER_PERIODE: one unit per month in which care was delivered; the tariff is the period price. Eenheid STUKS_OUTPUT only - LUMP_SUM: shares of a trajectory price on lifecycle events (JW305 / JW307), see `aandelen`. Eenheid EURO only; the price is the toewijzing\'s volume in eurocent - WEEKPRIJS: a fixed price per week, Monday to Sunday, each week declared in the month of its Sunday. Eenheid EURO only; the weekbedrag is the toewijzing\'s volume per week in eurocent - GEEN: taakgericht, nothing is declared through berichtenverkeer; carries no tariff 
 */

export const MunicipalDeclaratiemethode = {
    Volume: 'VOLUME',
    PerPeriode: 'PER_PERIODE',
    LumpSum: 'LUMP_SUM',
    Weekprijs: 'WEEKPRIJS',
    Geen: 'GEEN'
} as const;

export type MunicipalDeclaratiemethode = typeof MunicipalDeclaratiemethode[keyof typeof MunicipalDeclaratiemethode];


/**
 * The regime a gemeente contract is for. Fixed at creation.
 */

export const MunicipalFinancingRegime = {
    Jeugdwet: 'JEUGDWET',
    Wmo: 'WMO'
} as const;

export type MunicipalFinancingRegime = typeof MunicipalFinancingRegime[keyof typeof MunicipalFinancingRegime];


export interface MunicipalProductAandeel {
    'aandeelPct': number;
    'trigger': LumpSumTrigger;
    /**
     * For a STOP share, the JW307 redenen under which it is payable; empty means any. Must be empty for a START share. 
     */
    'vereisteStopredenen': Array<IjwRedenBeeindiging>;
}


/**
 * A maximum volume per frequentie; used to prefill and warn only, never to refuse.
 */
export interface MunicipalProductMaxOmvang {
    'volume': number;
    'eenheid': ToewijzingEenheid;
    'frequentie': ToewijzingFrequentie;
}


export interface MunicipalProductTarief {
    'geldigVanaf': string;
    /**
     * Inclusive; omit while the tariff is open-ended
     */
    'geldigTot'?: string | null;
    /**
     * The amount the contract states, in eurocent per `tariefEenheid` — an hourly rate is entered as the hourly rate, never divided by hand. 
     */
    'tariefEurocent': number;
    /**
     * The unit `tariefEurocent` is contracted per. Omit for the product\'s `eenheid`. The only other accepted value is UUR on a product delivered in MINUUT (\"Let op! Dit is een uurtarief\"); any other pair is refused with a 400 on `tarieven[n].tariefEenheid`. 
     */
    'tariefEenheid'?: ToewijzingEenheid;
}


export interface MunicipalProductTariefResponse {
    'geldigVanaf': string;
    'geldigTot'?: string | null;
    /**
     * The contracted amount, in eurocent per `tariefEenheid`
     */
    'tariefEurocent': number;
    'tariefEenheid': ToewijzingEenheid;
    /**
     * The ProductTarief a prestatie declares, in eurocent per unit of the product\'s `eenheid`. Equal to `tariefEurocent` unless the tariff is hourly and the product is delivered in minutes; then it is the rate ÷ 60 rounded once, half up, to whole eurocent (iJw IV081). 
     */
    'declarabelTariefEurocent': number;
}


export interface PlafondWarning {
    /**
     * Contract ID
     */
    'contractId': string;
    /**
     * Insurer name
     */
    'insurerName': string;
    /**
     * Percentage of ceiling used
     */
    'percentageUsed': number;
    /**
     * Total ceiling amount
     */
    'ceilingEuro': number;
    /**
     * Amount used so far
     */
    'usedEuro': number;
}
/**
 * RFC 7807 Problem Details for HTTP APIs
 */
export interface ProblemDetail {
    /**
     * URI reference identifying the problem type
     */
    'type': string;
    /**
     * Short, human-readable summary
     */
    'title': string;
    /**
     * HTTP status code
     */
    'status': number;
    /**
     * Human-readable explanation
     */
    'detail'?: string;
    /**
     * URI reference identifying the specific occurrence
     */
    'instance'?: string;
    /**
     * List of validation errors
     */
    'errors'?: Array<ProblemDetailErrorsInner>;
}
export interface ProblemDetailErrorsInner {
    /**
     * Field name that failed validation
     */
    'field'?: string;
    /**
     * Error message
     */
    'message'?: string;
}
/**
 * NZA profession code (beroep) for billing service code lookup: - ARTS_BIG3: Arts artikel 3 Wet BIG - ARTS_BIG14: Arts artikel 14 Wet BIG - GZ_PSYCH: GZ-psycholoog - KLIN_PSYCH: Klinisch psycholoog - PSYCHOTHER: Psychotherapeut - VERPL: Verpleegkundige - VERPL_SPEC: Verpleegkundig specialist - OVERIG: Overig 
 */

export const ProfessionCode = {
    ArtsBig3: 'ARTS_BIG3',
    ArtsBig14: 'ARTS_BIG14',
    GzPsych: 'GZ_PSYCH',
    KlinPsych: 'KLIN_PSYCH',
    Psychother: 'PSYCHOTHER',
    Verpl: 'VERPL',
    VerplSpec: 'VERPL_SPEC',
    Overig: 'OVERIG'
} as const;

export type ProfessionCode = typeof ProfessionCode[keyof typeof ProfessionCode];


export interface ServiceCodeResponse {
    /**
     * Service code unique identifier
     */
    'id': string;
    /**
     * Service code (e.g., \"CO0621\")
     */
    'code': string;
    /**
     * Service description
     */
    'description': string;
    'activityType': ActivityType;
    'serviceType': ServiceType;
    'setting'?: ServiceSetting;
    /**
     * ZPM profession code (beroep)
     */
    'professionCode'?: string | null;
    'consultType'?: ConsultType;
    /**
     * ZPM duration in minutes (e.g., 5, 15, 30, 45, 60, 75, 90, 120). Null for RESIDENTIAL codes (daily rate).
     */
    'durationMinutes'?: number | null;
    /**
     * Group size for GROUP service codes (2-10). Null for other service types.
     */
    'groupSize'?: number | null;
    'careIntensity'?: CareIntensity;
    /**
     * Valid from date
     */
    'validFrom': string;
    /**
     * Valid until date
     */
    'validUntil'?: string | null;
    /**
     * Whether service code is active
     */
    'isActive': boolean;
    /**
     * The code is invoiced to the cliënt and never declared to an insurer, and may be named as an afspraaktype\'s `fixedServiceCodeId` (OV0165 niet-basispakketzorg).
     */
    'clientInvoiced': boolean;
    /**
     * The NZa tariff a declaration for this code would be priced with. Resolved for the requested year, or — when that year has not been seeded — for the newest year the catalogue holds, which is the same fallback every pricing path takes. Null when the resolved year holds no tariff for this code.
     */
    'nzaTariffEuro'?: number | null;
    /**
     * Year `nzaTariffEuro` was resolved from. Not necessarily the year asked about: read it beside the amount, since a year behind the requested one means the catalogue is stale and every declaration is priced off it.
     */
    'nzaTariffYear'?: number | null;
}


/**
 * Setting (behandelsetting) for service codes in the Zorgprestatiemodel (ZPM). Settings are defined per NZA tariff structure: - AMBULANT_MONO: Ambulant monodisciplinair (outpatient monodisciplinary) - AMBULANT_MULTI: Ambulant multidisciplinair (outpatient multidisciplinary) - KLINISCH: Klinisch (inpatient clinical) - PRAKTIJK: Praktijk (private practice)  Note: GROEP (group therapy) is determined dynamically based on whether an appointment has multiple clients and is not user-selectable. 
 */

export const ServiceSetting = {
    AmbulantMono: 'AMBULANT_MONO',
    AmbulantMulti: 'AMBULANT_MULTI',
    Klinisch: 'KLINISCH',
    Praktijk: 'PRAKTIJK'
} as const;

export type ServiceSetting = typeof ServiceSetting[keyof typeof ServiceSetting];


/**
 * Type of service code in the Zorgprestatiemodel (ZPM): - CONSULTATION: Individual consultation (CO codes) - billed by setting, profession, consult type, duration - GROUP: Group session (GC codes) - billed by profession and group size (2-10) - RESIDENTIAL: Residential stay (VD codes) - billed by care intensity (A-E) - SURCHARGE: Toeslag (TC/TG codes) - declared alongside another prestatie, never on its own - OTHER: Overige prestatie (OV codes) - stands on its own, but is none of the three above 
 */

export const ServiceType = {
    Consultation: 'CONSULTATION',
    Group: 'GROUP',
    Residential: 'RESIDENTIAL',
    Surcharge: 'SURCHARGE',
    Other: 'OTHER'
} as const;

export type ServiceType = typeof ServiceType[keyof typeof ServiceType];


/**
 * RFC 7807 Problem Details extended with information about which settings are already covered by existing contracts. 
 */
export interface SettingConflictProblemDetail {
    /**
     * URI reference identifying the problem type
     */
    'type': string;
    /**
     * Short, human-readable summary
     */
    'title': string;
    /**
     * HTTP status code
     */
    'status': number;
    /**
     * Human-readable explanation
     */
    'detail'?: string;
    /**
     * URI reference identifying the specific occurrence
     */
    'instance'?: string;
    /**
     * List of settings that are already covered by existing contracts
     */
    'conflictingSettings': Array<ConflictingSetting>;
}
export interface TariffLookupRequest {
    /**
     * Insurer ID
     */
    'insurerId': string;
    /**
     * Service code ID
     */
    'serviceCodeId': string;
    /**
     * Optional setting ID for explicit setting-based lookup. Only used when the service code carries no setting of its own (a GROUP/GC code); a code with a setting is always looked up on that one. 
     */
    'settingId'?: string | null;
    /**
     * Beroep of the practitioner who delivers the consult (`users.profession_code`), which is what a beroep-scoped contract price rule is matched on. Omit it and no beroep rule can apply — the lookup falls through to the behandelsetting rule and then the contract default. Deliberately not the prestatiecode\'s own profession code: that describes the code, not the consult, and a RESIDENTIAL code carries none at all. 
     */
    'professionCode'?: string | null;
    /**
     * Date for lookup (defaults to today)
     */
    'date'?: string | null;
}
export interface TariffLookupResponse {
    /**
     * Insurer ID
     */
    'insurerId': string;
    /**
     * Insurer name
     */
    'insurerName': string;
    /**
     * Service code ID
     */
    'serviceCodeId': string;
    /**
     * Service code (e.g., \"180001\")
     */
    'serviceCode': string;
    /**
     * Service description
     */
    'serviceDescription': string;
    /**
     * Setting ID derived from the service code (if available)
     */
    'settingId'?: string | null;
    /**
     * Whether there\'s an active contract covering this setting
     */
    'hasContract': boolean;
    /**
     * ID of the contract used for pricing (if contracted)
     */
    'contractId'?: string | null;
    /**
     * NZa tariff in euros
     */
    'nzaTariffEuro': number;
    /**
     * Contract percentage (if contracted)
     */
    'contractPercentage'?: number | null;
    /**
     * Effective tariff after applying percentage
     */
    'effectiveTariffEuro': number;
    'billingType': BillingType;
}


/**
 * iJw `LDT_Eenheid`: MINUUT (01), UUR (04), ETMAAL (14), DAGDEEL (16, four hours), STUKS_OUTPUT (82), EURO (83), STUKS_INSPANNING (84) 
 */

export const ToewijzingEenheid = {
    Minuut: 'MINUUT',
    Uur: 'UUR',
    Etmaal: 'ETMAAL',
    Dagdeel: 'DAGDEEL',
    StuksOutput: 'STUKS_OUTPUT',
    Euro: 'EURO',
    StuksInspanning: 'STUKS_INSPANNING'
} as const;

export type ToewijzingEenheid = typeof ToewijzingEenheid[keyof typeof ToewijzingEenheid];


/**
 * iJw `LDT_Frequentie`: PER_DAG (1), PER_WEEK (2), PER_MAAND (4), TOTAAL (6, within the toewijzing\'s period) 
 */

export const ToewijzingFrequentie = {
    PerDag: 'PER_DAG',
    PerWeek: 'PER_WEEK',
    PerMaand: 'PER_MAAND',
    Totaal: 'TOTAAL'
} as const;

export type ToewijzingFrequentie = typeof ToewijzingFrequentie[keyof typeof ToewijzingFrequentie];


/**
 * The bekostigingsvariant a product is bought under.
 */

export const Uitvoeringsvariant = {
    Inspanningsgericht: 'INSPANNINGSGERICHT',
    Outputgericht: 'OUTPUTGERICHT',
    Taakgericht: 'TAAKGERICHT'
} as const;

export type Uitvoeringsvariant = typeof Uitvoeringsvariant[keyof typeof Uitvoeringsvariant];


/**
 * All fields are optional; only provided fields are updated. The rule\'s scope is not among them — see `updateContractPriceRule`.  The result must still price something: an update that clears both `percentageOfNza` and `fixedTariffEuro` is a 400, not a rule that charges nothing. 
 */
export interface UpdateContractPriceRuleRequest {
    /**
     * Percentage of the NZa maximum this scope is agreed at
     */
    'percentageOfNza'?: number | null;
    /**
     * An agreed amount in euro, which wins over `percentageOfNza` when both are set
     */
    'fixedTariffEuro'?: number | null;
    /**
     * Notes for this rule
     */
    'notes'?: string | null;
}
/**
 * All fields are optional. Only provided fields will be updated.
 */
export interface UpdateContractRequest {
    /**
     * Optional location ID for AGB code. When null, Organization.agbCode is used as fallback.
     */
    'locationId'?: string | null;
    /**
     * Contract reference number
     */
    'contractNumber'?: string | null;
    /**
     * Date when this contract takes effect
     */
    'effectiveDate'?: string | null;
    /**
     * Contract start date
     */
    'validFrom'?: string | null;
    /**
     * Contract end date
     */
    'validUntil'?: string | null;
    /**
     * Optional ceiling/plafond amount in euros
     */
    'ceilingEuro'?: number | null;
    /**
     * Ceiling type
     */
    'ceilingType'?: string | null;
    /**
     * Contract notes
     */
    'notes'?: string | null;
    'sendMethod'?: ContractSendMethod;
}


/**
 * All fields are optional. Only provided fields will be updated.
 */
export interface UpdateContractSettingRequest {
    /**
     * Percentage of NZa tariff (e.g., 95.0)
     */
    'percentageOfNza'?: number | null;
    /**
     * Optional fixed tariff override
     */
    'fixedTariffEuro'?: number | null;
    /**
     * Notes for this setting
     */
    'notes'?: string | null;
}
/**
 * Replaces the header and the gemeenten. An optional field left out is cleared; the financing regime cannot be changed. 
 */
export interface UpdateMunicipalContractRequest {
    'contractNumber'?: string | null;
    /**
     * The jeugdregio the gemeenten were filled from (`GET /api/v1/jeugdregios`), recorded for display only — `gemeentecodes` is what the contract covers, and need not equal the region\'s gemeenten. Omit or null when the gemeenten were picked one by one. 
     */
    'jeugdregioCode'?: string | null;
    'validFrom': string;
    'validUntil'?: string | null;
    'notes'?: string | null;
    'gemeentecodes': Array<string>;
}

/**
 * ContractsApi - axios parameter creator
 */
export const ContractsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Adds one priced scope to the contract. A contract carries at most one rule per scope value — one rule per prestatiecode, one per beroep, and one default — so a second rule for the same scope value is a 409.  `SETTING`-scoped rules are **derived** from the contract\'s behandelsettings and cannot be written here; use the settings endpoints instead. 
         * @summary Add a price rule to a contract
         * @param {string} contractId Contract UUID
         * @param {ContractPriceRuleRequest} contractPriceRuleRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        addContractPriceRule: async (contractId: string, contractPriceRuleRequest: ContractPriceRuleRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'contractId' is not null or undefined
            assertParamExists('addContractPriceRule', 'contractId', contractId)
            // verify required parameter 'contractPriceRuleRequest' is not null or undefined
            assertParamExists('addContractPriceRule', 'contractPriceRuleRequest', contractPriceRuleRequest)
            const localVarPath = `/api/v1/contracts/{contractId}/price-rules`
                .replace(`{${"contractId"}}`, encodeURIComponent(String(contractId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(contractPriceRuleRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Add a setting (behandelsetting) with pricing to a contract. Each setting can only be added once per contract. 
         * @summary Add setting to contract
         * @param {string} contractId Contract UUID
         * @param {ContractSettingRequest} contractSettingRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        addContractSetting: async (contractId: string, contractSettingRequest: ContractSettingRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'contractId' is not null or undefined
            assertParamExists('addContractSetting', 'contractId', contractId)
            // verify required parameter 'contractSettingRequest' is not null or undefined
            assertParamExists('addContractSetting', 'contractSettingRequest', contractSettingRequest)
            const localVarPath = `/api/v1/contracts/{contractId}/settings`
                .replace(`{${"contractId"}}`, encodeURIComponent(String(contractId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(contractSettingRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Adds the maximum budget the contract allows for one productcategorie over one period — the ceiling an aspecifieke toewijzing may request. See `MunicipalCategoryBudgetRequest` for what is refused. 
         * @summary Add a category budget to a gemeente contract
         * @param {string} contractId Gemeente contract ID
         * @param {MunicipalCategoryBudgetRequest} municipalCategoryBudgetRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        addMunicipalContractCategoryBudget: async (contractId: string, municipalCategoryBudgetRequest: MunicipalCategoryBudgetRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'contractId' is not null or undefined
            assertParamExists('addMunicipalContractCategoryBudget', 'contractId', contractId)
            // verify required parameter 'municipalCategoryBudgetRequest' is not null or undefined
            assertParamExists('addMunicipalContractCategoryBudget', 'municipalCategoryBudgetRequest', municipalCategoryBudgetRequest)
            const localVarPath = `/api/v1/municipal-contracts/{contractId}/category-budgets`
                .replace(`{${"contractId"}}`, encodeURIComponent(String(contractId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(municipalCategoryBudgetRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Adds one product with its dated tariffs and, for a LUMP_SUM product, its shares. See `MunicipalContractProductRequest` for what is refused. 
         * @summary Add a product to a gemeente contract
         * @param {string} contractId Gemeente contract ID
         * @param {MunicipalContractProductRequest} municipalContractProductRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        addMunicipalContractProduct: async (contractId: string, municipalContractProductRequest: MunicipalContractProductRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'contractId' is not null or undefined
            assertParamExists('addMunicipalContractProduct', 'contractId', contractId)
            // verify required parameter 'municipalContractProductRequest' is not null or undefined
            assertParamExists('addMunicipalContractProduct', 'municipalContractProductRequest', municipalContractProductRequest)
            const localVarPath = `/api/v1/municipal-contracts/{contractId}/products`
                .replace(`{${"contractId"}}`, encodeURIComponent(String(contractId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(municipalContractProductRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Creates a new contract with an insurer. Contracts are created in ACTIVE status.  When creating a new contract for an insurer where an active contract already exists, the existing contract is automatically expired at the day before the new contract\'s effective date. 
         * @summary Create new contract
         * @param {CreateContractRequest} createContractRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createContract: async (createContractRequest: CreateContractRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'createContractRequest' is not null or undefined
            assertParamExists('createContract', 'createContractRequest', createContractRequest)
            const localVarPath = `/api/v1/contracts`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(createContractRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Creates the contract a practice holds with one or more gemeenten for Jeugdwet or Wmo care (#4746): the gemeenten it covers, the products it holds, how each product is declared and at what tariff on which date. Created in ACTIVE status.  Refused with a 400 when the organisation has not enabled the contract\'s financing regime in Organisatiebeheer › Bekostiging. Every validation failure is a 400 whose `errors` array names the offending field.  Nothing is seeded: the practice enters its own contract. A gemeente contract is not an insurer contract — it does not appear under `/api/v1/contracts`, and that path answers 404 for its id. 
         * @summary Create a gemeente contract
         * @param {CreateMunicipalContractRequest} createMunicipalContractRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createMunicipalContract: async (createMunicipalContractRequest: CreateMunicipalContractRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'createMunicipalContractRequest' is not null or undefined
            assertParamExists('createMunicipalContract', 'createMunicipalContractRequest', createMunicipalContractRequest)
            const localVarPath = `/api/v1/municipal-contracts`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(createMunicipalContractRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Deletes a contract. This always succeeds, whatever the contract has behind it — consults, draft invoices, sent invoices, an expired status.  A deleted contract counts nowhere: it prices no consult, appears in no list (`includeExpired` included) and is reported as no contract on the declaratie overview. Openstaande consults that were priced under it resolve to whatever now applies — the organisation\'s own tariff if it set one, else the full NZa maximum.  Lines on invoices that have already been sent are **not** rewritten: each carries the amount and the contract it was priced under. Correcting one of those is the credit-note flow, not this.  Deleting is not reversible. 
         * @summary Delete contract
         * @param {string} contractId Contract UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteContract: async (contractId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'contractId' is not null or undefined
            assertParamExists('deleteContract', 'contractId', contractId)
            const localVarPath = `/api/v1/contracts/{contractId}`
                .replace(`{${"contractId"}}`, encodeURIComponent(String(contractId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Removes one priced scope. What the rule priced then falls through to the next rule down the precedence, and a line no rule matches is uncontracted — the full NZa maximum, never a discount nobody agreed (#3822). 
         * @summary Remove a price rule from a contract
         * @param {string} contractId Contract UUID
         * @param {string} priceRuleId Price rule UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteContractPriceRule: async (contractId: string, priceRuleId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'contractId' is not null or undefined
            assertParamExists('deleteContractPriceRule', 'contractId', contractId)
            // verify required parameter 'priceRuleId' is not null or undefined
            assertParamExists('deleteContractPriceRule', 'priceRuleId', priceRuleId)
            const localVarPath = `/api/v1/contracts/{contractId}/price-rules/{priceRuleId}`
                .replace(`{${"contractId"}}`, encodeURIComponent(String(contractId)))
                .replace(`{${"priceRuleId"}}`, encodeURIComponent(String(priceRuleId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Remove a setting (behandelsetting) from a contract.
         * @summary Remove setting from contract
         * @param {string} contractId Contract UUID
         * @param {string} settingId Contract setting UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteContractSetting: async (contractId: string, settingId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'contractId' is not null or undefined
            assertParamExists('deleteContractSetting', 'contractId', contractId)
            // verify required parameter 'settingId' is not null or undefined
            assertParamExists('deleteContractSetting', 'settingId', settingId)
            const localVarPath = `/api/v1/contracts/{contractId}/settings/{settingId}`
                .replace(`{${"contractId"}}`, encodeURIComponent(String(contractId)))
                .replace(`{${"settingId"}}`, encodeURIComponent(String(settingId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Soft-deletes the contract, as for an insurer contract (#4555): it covers nothing from then on and is no longer listed. 
         * @summary Delete a gemeente contract
         * @param {string} contractId Gemeente contract ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteMunicipalContract: async (contractId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'contractId' is not null or undefined
            assertParamExists('deleteMunicipalContract', 'contractId', contractId)
            const localVarPath = `/api/v1/municipal-contracts/{contractId}`
                .replace(`{${"contractId"}}`, encodeURIComponent(String(contractId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Remove a category budget from a gemeente contract
         * @param {string} contractId Gemeente contract ID
         * @param {string} budgetId Category budget ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteMunicipalContractCategoryBudget: async (contractId: string, budgetId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'contractId' is not null or undefined
            assertParamExists('deleteMunicipalContractCategoryBudget', 'contractId', contractId)
            // verify required parameter 'budgetId' is not null or undefined
            assertParamExists('deleteMunicipalContractCategoryBudget', 'budgetId', budgetId)
            const localVarPath = `/api/v1/municipal-contracts/{contractId}/category-budgets/{budgetId}`
                .replace(`{${"contractId"}}`, encodeURIComponent(String(contractId)))
                .replace(`{${"budgetId"}}`, encodeURIComponent(String(budgetId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Remove a product from a gemeente contract
         * @param {string} contractId Gemeente contract ID
         * @param {string} productId Product ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteMunicipalContractProduct: async (contractId: string, productId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'contractId' is not null or undefined
            assertParamExists('deleteMunicipalContractProduct', 'contractId', contractId)
            // verify required parameter 'productId' is not null or undefined
            assertParamExists('deleteMunicipalContractProduct', 'productId', productId)
            const localVarPath = `/api/v1/municipal-contracts/{contractId}/products/{productId}`
                .replace(`{${"contractId"}}`, encodeURIComponent(String(contractId)))
                .replace(`{${"productId"}}`, encodeURIComponent(String(productId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Manually expires an active contract. Sets the status to EXPIRED and optionally updates the end date. 
         * @summary Expire contract
         * @param {string} contractId Contract UUID
         * @param {string} [endDate] End date (defaults to today if not provided)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        expireContract: async (contractId: string, endDate?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'contractId' is not null or undefined
            assertParamExists('expireContract', 'contractId', contractId)
            const localVarPath = `/api/v1/contracts/{contractId}/expire`
                .replace(`{${"contractId"}}`, encodeURIComponent(String(contractId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (endDate !== undefined) {
                localVarQueryParameter['endDate'] = (endDate as any instanceof Date) ?
                    (endDate as any).toISOString().substring(0,10) :
                    endDate;
            }

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieves a single contract by ID. User must be in same organization.
         * @summary Get contract by ID
         * @param {string} contractId Contract UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getContractById: async (contractId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'contractId' is not null or undefined
            assertParamExists('getContractById', 'contractId', contractId)
            const localVarPath = `/api/v1/contracts/{contractId}`
                .replace(`{${"contractId"}}`, encodeURIComponent(String(contractId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns dashboard summary data including active contract count, expiring contracts, and plafond warnings. 
         * @summary Get contract summary
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getContractSummary: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/contracts/summary`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Get a gemeente contract
         * @param {string} contractId Gemeente contract ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getMunicipalContract: async (contractId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'contractId' is not null or undefined
            assertParamExists('getMunicipalContract', 'contractId', contractId)
            const localVarPath = `/api/v1/municipal-contracts/{contractId}`
                .replace(`{${"contractId"}}`, encodeURIComponent(String(contractId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Lists the contract\'s price rules (#4025) — one row per priced scope, resolved most-specific-wins: prestatiecode, then beroep, then behandelsetting, then the contract\'s default.  `scope` narrows the answer to one of those. It is not merely a convenience: a contract that prices per prestatiecode carries 222+ rules, and the beroep screen wants at most nine of them.  The answer is **not paged**, and that is bounded by the scopes that have a screen today: a contract carries one default and at most one rule per beroep. The prestatiecode screen (#4030) is the 222-row consumer, and it needs a page cut in the database rather than out of this list — do not reuse this operation for it. 
         * @summary List price rules of a contract
         * @param {string} contractId Contract UUID
         * @param {ContractPriceScope} [scope] Return only the rules of this scope. Omit for every rule of the contract.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listContractPriceRules: async (contractId: string, scope?: ContractPriceScope, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'contractId' is not null or undefined
            assertParamExists('listContractPriceRules', 'contractId', contractId)
            const localVarPath = `/api/v1/contracts/{contractId}/price-rules`
                .replace(`{${"contractId"}}`, encodeURIComponent(String(contractId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (scope !== undefined) {
                localVarQueryParameter['scope'] = scope;
            }

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Lists prestatiecodes with the amount this contract agrees for each one, one page at a time.  This is `GET /price-rules?scope=SERVICE_CODE` read at a different grain, and the grain is the point: that one answers with the *rules*, this one with the **codes**. An a.s.r. or Zorg en Zekerheid agreement names 222-225 of them and the practice reconciles the list against the insurer\'s own spreadsheet, so a code with no agreed amount has to be visible beside one agreed at € 0,00 — and 222 rules is past the size at which an unpaged, unsearchable list is usable. `priceRuleId` is `null` for the first and set for the second; they are different facts and are never conflated.  Writing an agreement is `POST`/`PUT`/`DELETE /api/v1/contracts/{contractId}/price-rules` and nowhere else, which is why `priceRuleId` is on every agreed row: there is one write path per scope, and this endpoint is not it.  Use `agreedOnly` to narrow the page to the codes this contract actually prices. It combines with every structural filter, which is what makes \"GC codes with no agreed tariff\" — the question that finds a half-loaded tariff sheet — askable at all (#4048).  `coverage` answers the same question without paging: agreed against total per soort prestatie, over the filtered set. It deliberately ignores `agreedOnly`, because that parameter selects on the very thing coverage measures and every group would otherwise report \"n van n\". 
         * @summary List the contract\'s agreed tariff per prestatiecode
         * @param {string} contractId Contract UUID
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Number of items per page
         * @param {string} [search] Search by prestatiecode or description
         * @param {boolean} [agreedOnly] Only return codes this contract has an agreed tariff for
         * @param {ServiceType} [serviceType] Only prestatiecodes of this soort prestatie — CONSULTATION (CO), GROUP (GC) or RESIDENTIAL (VD). 
         * @param {ServiceSetting} [setting] Only prestatiecodes for this behandelsetting. GROUP codes carry none and are never matched by it — a group consult is priced against the cliënt\&#39;s own zorgtraject setting. 
         * @param {ProfessionCode} [professionCode] Only prestatiecodes for this beroep
         * @param {ConsultType} [consultType] Only prestatiecodes for this consulttype
         * @param {number} [durationMinutes] Only prestatiecodes of this duration. The ZPM buckets are 5, 15, 30, 45, 60, 75, 90 and 120, but any positive number is accepted: which durations the catalogue holds is data rather than contract. 
         * @param {string} [validOn] Only prestatiecodes whose validity window covers this date. The catalogue is versioned per year, so this is how a practice narrows a 589-code list to the codes that were declarable in the year the agreement covers.  It says nothing about &#x60;isActive&#x60;: a retired code this contract prices is still listed, which is what keeps a rule that is doing something visible. 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listContractServiceCodeTariffs: async (contractId: string, page?: number, size?: number, search?: string, agreedOnly?: boolean, serviceType?: ServiceType, setting?: ServiceSetting, professionCode?: ProfessionCode, consultType?: ConsultType, durationMinutes?: number, validOn?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'contractId' is not null or undefined
            assertParamExists('listContractServiceCodeTariffs', 'contractId', contractId)
            const localVarPath = `/api/v1/contracts/{contractId}/service-code-tariffs`
                .replace(`{${"contractId"}}`, encodeURIComponent(String(contractId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (page !== undefined) {
                localVarQueryParameter['page'] = page;
            }

            if (size !== undefined) {
                localVarQueryParameter['size'] = size;
            }

            if (search !== undefined) {
                localVarQueryParameter['search'] = search;
            }

            if (agreedOnly !== undefined) {
                localVarQueryParameter['agreedOnly'] = agreedOnly;
            }

            if (serviceType !== undefined) {
                localVarQueryParameter['serviceType'] = serviceType;
            }

            if (setting !== undefined) {
                localVarQueryParameter['setting'] = setting;
            }

            if (professionCode !== undefined) {
                localVarQueryParameter['professionCode'] = professionCode;
            }

            if (consultType !== undefined) {
                localVarQueryParameter['consultType'] = consultType;
            }

            if (durationMinutes !== undefined) {
                localVarQueryParameter['durationMinutes'] = durationMinutes;
            }

            if (validOn !== undefined) {
                localVarQueryParameter['validOn'] = (validOn as any instanceof Date) ?
                    (validOn as any).toISOString().substring(0,10) :
                    validOn;
            }

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Get all settings (behandelsettings) and their pricing for a contract. Settings define coverage/pricing at the behandelsetting level (e.g., \"Klinisch at 90%\"). 
         * @summary List contract settings
         * @param {string} contractId Contract UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listContractSettings: async (contractId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'contractId' is not null or undefined
            assertParamExists('listContractSettings', 'contractId', contractId)
            const localVarPath = `/api/v1/contracts/{contractId}/settings`
                .replace(`{${"contractId"}}`, encodeURIComponent(String(contractId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * List contracts in the authenticated user\'s organization with pagination, search, and filtering capabilities.  Multiple contracts per insurer are allowed, each covering different settings. Use the settingId parameter to find contracts covering a specific setting. 
         * @summary List contracts
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Number of items per page
         * @param {string} [sort] Sort specification as \&quot;field,direction\&quot; (e.g., \&quot;effectiveDate,desc\&quot;). Supported fields: effectiveDate, validFrom, validUntil, contractNumber, status, ceilingEuro, createdAt, updatedAt. Any other field falls back to effectiveDate.
         * @param {ContractStatus} [status] Filter by contract status
         * @param {string} [insurerId] Filter by insurer ID - returns every contract that covers this insurer, which includes a contract covering the concern the insurer belongs to. 
         * @param {string} [settingId] Filter by setting ID - returns contracts that cover this setting
         * @param {boolean} [includeExpired] Include expired contracts (default false)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listContracts: async (page?: number, size?: number, sort?: string, status?: ContractStatus, insurerId?: string, settingId?: string, includeExpired?: boolean, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/contracts`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (page !== undefined) {
                localVarQueryParameter['page'] = page;
            }

            if (size !== undefined) {
                localVarQueryParameter['size'] = size;
            }

            if (sort !== undefined) {
                localVarQueryParameter['sort'] = sort;
            }

            if (status !== undefined) {
                localVarQueryParameter['status'] = status;
            }

            if (insurerId !== undefined) {
                localVarQueryParameter['insurerId'] = insurerId;
            }

            if (settingId !== undefined) {
                localVarQueryParameter['settingId'] = settingId;
            }

            if (includeExpired !== undefined) {
                localVarQueryParameter['includeExpired'] = includeExpired;
            }

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * The organisation\'s gemeente contracts, most recent first, each with its gemeenten and products. Unpaged: a practice holds a handful. 
         * @summary List gemeente contracts
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listMunicipalContracts: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/municipal-contracts`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Looks up the effective tariff for a specific insurer and service code. Returns whether there\'s a contract, the NZa tariff, and the effective rate.  The lookup finds the active contract that covers the setting associated with the service code. Since multiple contracts per insurer are allowed (each covering different settings), the response includes the contractId used for pricing. 
         * @summary Look up tariff for billing
         * @param {TariffLookupRequest} tariffLookupRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        lookupTariff: async (tariffLookupRequest: TariffLookupRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'tariffLookupRequest' is not null or undefined
            assertParamExists('lookupTariff', 'tariffLookupRequest', tariffLookupRequest)
            const localVarPath = `/api/v1/contracts/lookup-tariff`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(tariffLookupRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Updates an existing contract. Who it is with cannot be changed — neither `insurerId` nor `concernId` is updatable, because moving a live agreement between labels would silently reprice every consult already declared under it. 
         * @summary Update contract
         * @param {string} contractId Contract UUID
         * @param {UpdateContractRequest} updateContractRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateContract: async (contractId: string, updateContractRequest: UpdateContractRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'contractId' is not null or undefined
            assertParamExists('updateContract', 'contractId', contractId)
            // verify required parameter 'updateContractRequest' is not null or undefined
            assertParamExists('updateContract', 'updateContractRequest', updateContractRequest)
            const localVarPath = `/api/v1/contracts/{contractId}`
                .replace(`{${"contractId"}}`, encodeURIComponent(String(contractId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(updateContractRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Updates what a price rule charges. Its scope is fixed: moving a rule from one beroep to another is a delete plus an add, so that the uniqueness of the scope it lands on is decided the same way it was on creation. 
         * @summary Update a price rule
         * @param {string} contractId Contract UUID
         * @param {string} priceRuleId Price rule UUID
         * @param {UpdateContractPriceRuleRequest} updateContractPriceRuleRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateContractPriceRule: async (contractId: string, priceRuleId: string, updateContractPriceRuleRequest: UpdateContractPriceRuleRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'contractId' is not null or undefined
            assertParamExists('updateContractPriceRule', 'contractId', contractId)
            // verify required parameter 'priceRuleId' is not null or undefined
            assertParamExists('updateContractPriceRule', 'priceRuleId', priceRuleId)
            // verify required parameter 'updateContractPriceRuleRequest' is not null or undefined
            assertParamExists('updateContractPriceRule', 'updateContractPriceRuleRequest', updateContractPriceRuleRequest)
            const localVarPath = `/api/v1/contracts/{contractId}/price-rules/{priceRuleId}`
                .replace(`{${"contractId"}}`, encodeURIComponent(String(contractId)))
                .replace(`{${"priceRuleId"}}`, encodeURIComponent(String(priceRuleId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(updateContractPriceRuleRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Update pricing for an existing setting within a contract.
         * @summary Update contract setting
         * @param {string} contractId Contract UUID
         * @param {string} settingId Contract setting UUID
         * @param {UpdateContractSettingRequest} updateContractSettingRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateContractSetting: async (contractId: string, settingId: string, updateContractSettingRequest: UpdateContractSettingRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'contractId' is not null or undefined
            assertParamExists('updateContractSetting', 'contractId', contractId)
            // verify required parameter 'settingId' is not null or undefined
            assertParamExists('updateContractSetting', 'settingId', settingId)
            // verify required parameter 'updateContractSettingRequest' is not null or undefined
            assertParamExists('updateContractSetting', 'updateContractSettingRequest', updateContractSettingRequest)
            const localVarPath = `/api/v1/contracts/{contractId}/settings/{settingId}`
                .replace(`{${"contractId"}}`, encodeURIComponent(String(contractId)))
                .replace(`{${"settingId"}}`, encodeURIComponent(String(settingId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(updateContractSettingRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Replaces the contract\'s header and the set of gemeenten it covers. An optional field left out is cleared. The financing regime is fixed at creation; the products are written through their own operations. 
         * @summary Replace a gemeente contract\'s header and gemeenten
         * @param {string} contractId Gemeente contract ID
         * @param {UpdateMunicipalContractRequest} updateMunicipalContractRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateMunicipalContract: async (contractId: string, updateMunicipalContractRequest: UpdateMunicipalContractRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'contractId' is not null or undefined
            assertParamExists('updateMunicipalContract', 'contractId', contractId)
            // verify required parameter 'updateMunicipalContractRequest' is not null or undefined
            assertParamExists('updateMunicipalContract', 'updateMunicipalContractRequest', updateMunicipalContractRequest)
            const localVarPath = `/api/v1/municipal-contracts/{contractId}`
                .replace(`{${"contractId"}}`, encodeURIComponent(String(contractId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(updateMunicipalContractRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Replaces the categorie, period and amount. A yearly indexation is a new budget, not an edited amount. 
         * @summary Replace a gemeente contract category budget
         * @param {string} contractId Gemeente contract ID
         * @param {string} budgetId Category budget ID
         * @param {MunicipalCategoryBudgetRequest} municipalCategoryBudgetRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateMunicipalContractCategoryBudget: async (contractId: string, budgetId: string, municipalCategoryBudgetRequest: MunicipalCategoryBudgetRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'contractId' is not null or undefined
            assertParamExists('updateMunicipalContractCategoryBudget', 'contractId', contractId)
            // verify required parameter 'budgetId' is not null or undefined
            assertParamExists('updateMunicipalContractCategoryBudget', 'budgetId', budgetId)
            // verify required parameter 'municipalCategoryBudgetRequest' is not null or undefined
            assertParamExists('updateMunicipalContractCategoryBudget', 'municipalCategoryBudgetRequest', municipalCategoryBudgetRequest)
            const localVarPath = `/api/v1/municipal-contracts/{contractId}/category-budgets/{budgetId}`
                .replace(`{${"contractId"}}`, encodeURIComponent(String(contractId)))
                .replace(`{${"budgetId"}}`, encodeURIComponent(String(budgetId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(municipalCategoryBudgetRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Replaces the product, its tariffs and its shares as one document, validated as a whole. A new indexation is a new entry in `tarieven`, not an edited amount. 
         * @summary Replace a gemeente contract product
         * @param {string} contractId Gemeente contract ID
         * @param {string} productId Product ID
         * @param {MunicipalContractProductRequest} municipalContractProductRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateMunicipalContractProduct: async (contractId: string, productId: string, municipalContractProductRequest: MunicipalContractProductRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'contractId' is not null or undefined
            assertParamExists('updateMunicipalContractProduct', 'contractId', contractId)
            // verify required parameter 'productId' is not null or undefined
            assertParamExists('updateMunicipalContractProduct', 'productId', productId)
            // verify required parameter 'municipalContractProductRequest' is not null or undefined
            assertParamExists('updateMunicipalContractProduct', 'municipalContractProductRequest', municipalContractProductRequest)
            const localVarPath = `/api/v1/municipal-contracts/{contractId}/products/{productId}`
                .replace(`{${"contractId"}}`, encodeURIComponent(String(contractId)))
                .replace(`{${"productId"}}`, encodeURIComponent(String(productId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(municipalContractProductRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * ContractsApi - functional programming interface
 */
export const ContractsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = ContractsApiAxiosParamCreator(configuration)
    return {
        /**
         * Adds one priced scope to the contract. A contract carries at most one rule per scope value — one rule per prestatiecode, one per beroep, and one default — so a second rule for the same scope value is a 409.  `SETTING`-scoped rules are **derived** from the contract\'s behandelsettings and cannot be written here; use the settings endpoints instead. 
         * @summary Add a price rule to a contract
         * @param {string} contractId Contract UUID
         * @param {ContractPriceRuleRequest} contractPriceRuleRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async addContractPriceRule(contractId: string, contractPriceRuleRequest: ContractPriceRuleRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ContractPriceRuleResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.addContractPriceRule(contractId, contractPriceRuleRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContractsApi.addContractPriceRule']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Add a setting (behandelsetting) with pricing to a contract. Each setting can only be added once per contract. 
         * @summary Add setting to contract
         * @param {string} contractId Contract UUID
         * @param {ContractSettingRequest} contractSettingRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async addContractSetting(contractId: string, contractSettingRequest: ContractSettingRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ContractSettingResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.addContractSetting(contractId, contractSettingRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContractsApi.addContractSetting']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Adds the maximum budget the contract allows for one productcategorie over one period — the ceiling an aspecifieke toewijzing may request. See `MunicipalCategoryBudgetRequest` for what is refused. 
         * @summary Add a category budget to a gemeente contract
         * @param {string} contractId Gemeente contract ID
         * @param {MunicipalCategoryBudgetRequest} municipalCategoryBudgetRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async addMunicipalContractCategoryBudget(contractId: string, municipalCategoryBudgetRequest: MunicipalCategoryBudgetRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<MunicipalCategoryBudgetResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.addMunicipalContractCategoryBudget(contractId, municipalCategoryBudgetRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContractsApi.addMunicipalContractCategoryBudget']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Adds one product with its dated tariffs and, for a LUMP_SUM product, its shares. See `MunicipalContractProductRequest` for what is refused. 
         * @summary Add a product to a gemeente contract
         * @param {string} contractId Gemeente contract ID
         * @param {MunicipalContractProductRequest} municipalContractProductRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async addMunicipalContractProduct(contractId: string, municipalContractProductRequest: MunicipalContractProductRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<MunicipalContractProductResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.addMunicipalContractProduct(contractId, municipalContractProductRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContractsApi.addMunicipalContractProduct']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Creates a new contract with an insurer. Contracts are created in ACTIVE status.  When creating a new contract for an insurer where an active contract already exists, the existing contract is automatically expired at the day before the new contract\'s effective date. 
         * @summary Create new contract
         * @param {CreateContractRequest} createContractRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createContract(createContractRequest: CreateContractRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ContractResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createContract(createContractRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContractsApi.createContract']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Creates the contract a practice holds with one or more gemeenten for Jeugdwet or Wmo care (#4746): the gemeenten it covers, the products it holds, how each product is declared and at what tariff on which date. Created in ACTIVE status.  Refused with a 400 when the organisation has not enabled the contract\'s financing regime in Organisatiebeheer › Bekostiging. Every validation failure is a 400 whose `errors` array names the offending field.  Nothing is seeded: the practice enters its own contract. A gemeente contract is not an insurer contract — it does not appear under `/api/v1/contracts`, and that path answers 404 for its id. 
         * @summary Create a gemeente contract
         * @param {CreateMunicipalContractRequest} createMunicipalContractRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createMunicipalContract(createMunicipalContractRequest: CreateMunicipalContractRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<MunicipalContractResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createMunicipalContract(createMunicipalContractRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContractsApi.createMunicipalContract']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Deletes a contract. This always succeeds, whatever the contract has behind it — consults, draft invoices, sent invoices, an expired status.  A deleted contract counts nowhere: it prices no consult, appears in no list (`includeExpired` included) and is reported as no contract on the declaratie overview. Openstaande consults that were priced under it resolve to whatever now applies — the organisation\'s own tariff if it set one, else the full NZa maximum.  Lines on invoices that have already been sent are **not** rewritten: each carries the amount and the contract it was priced under. Correcting one of those is the credit-note flow, not this.  Deleting is not reversible. 
         * @summary Delete contract
         * @param {string} contractId Contract UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteContract(contractId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteContract(contractId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContractsApi.deleteContract']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Removes one priced scope. What the rule priced then falls through to the next rule down the precedence, and a line no rule matches is uncontracted — the full NZa maximum, never a discount nobody agreed (#3822). 
         * @summary Remove a price rule from a contract
         * @param {string} contractId Contract UUID
         * @param {string} priceRuleId Price rule UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteContractPriceRule(contractId: string, priceRuleId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteContractPriceRule(contractId, priceRuleId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContractsApi.deleteContractPriceRule']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Remove a setting (behandelsetting) from a contract.
         * @summary Remove setting from contract
         * @param {string} contractId Contract UUID
         * @param {string} settingId Contract setting UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteContractSetting(contractId: string, settingId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteContractSetting(contractId, settingId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContractsApi.deleteContractSetting']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Soft-deletes the contract, as for an insurer contract (#4555): it covers nothing from then on and is no longer listed. 
         * @summary Delete a gemeente contract
         * @param {string} contractId Gemeente contract ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteMunicipalContract(contractId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteMunicipalContract(contractId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContractsApi.deleteMunicipalContract']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Remove a category budget from a gemeente contract
         * @param {string} contractId Gemeente contract ID
         * @param {string} budgetId Category budget ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteMunicipalContractCategoryBudget(contractId: string, budgetId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteMunicipalContractCategoryBudget(contractId, budgetId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContractsApi.deleteMunicipalContractCategoryBudget']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Remove a product from a gemeente contract
         * @param {string} contractId Gemeente contract ID
         * @param {string} productId Product ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteMunicipalContractProduct(contractId: string, productId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteMunicipalContractProduct(contractId, productId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContractsApi.deleteMunicipalContractProduct']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Manually expires an active contract. Sets the status to EXPIRED and optionally updates the end date. 
         * @summary Expire contract
         * @param {string} contractId Contract UUID
         * @param {string} [endDate] End date (defaults to today if not provided)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async expireContract(contractId: string, endDate?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ContractResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.expireContract(contractId, endDate, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContractsApi.expireContract']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Retrieves a single contract by ID. User must be in same organization.
         * @summary Get contract by ID
         * @param {string} contractId Contract UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getContractById(contractId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ContractResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getContractById(contractId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContractsApi.getContractById']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns dashboard summary data including active contract count, expiring contracts, and plafond warnings. 
         * @summary Get contract summary
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getContractSummary(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ContractSummaryResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getContractSummary(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContractsApi.getContractSummary']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Get a gemeente contract
         * @param {string} contractId Gemeente contract ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getMunicipalContract(contractId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<MunicipalContractResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getMunicipalContract(contractId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContractsApi.getMunicipalContract']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Lists the contract\'s price rules (#4025) — one row per priced scope, resolved most-specific-wins: prestatiecode, then beroep, then behandelsetting, then the contract\'s default.  `scope` narrows the answer to one of those. It is not merely a convenience: a contract that prices per prestatiecode carries 222+ rules, and the beroep screen wants at most nine of them.  The answer is **not paged**, and that is bounded by the scopes that have a screen today: a contract carries one default and at most one rule per beroep. The prestatiecode screen (#4030) is the 222-row consumer, and it needs a page cut in the database rather than out of this list — do not reuse this operation for it. 
         * @summary List price rules of a contract
         * @param {string} contractId Contract UUID
         * @param {ContractPriceScope} [scope] Return only the rules of this scope. Omit for every rule of the contract.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listContractPriceRules(contractId: string, scope?: ContractPriceScope, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<ContractPriceRuleResponse>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listContractPriceRules(contractId, scope, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContractsApi.listContractPriceRules']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Lists prestatiecodes with the amount this contract agrees for each one, one page at a time.  This is `GET /price-rules?scope=SERVICE_CODE` read at a different grain, and the grain is the point: that one answers with the *rules*, this one with the **codes**. An a.s.r. or Zorg en Zekerheid agreement names 222-225 of them and the practice reconciles the list against the insurer\'s own spreadsheet, so a code with no agreed amount has to be visible beside one agreed at € 0,00 — and 222 rules is past the size at which an unpaged, unsearchable list is usable. `priceRuleId` is `null` for the first and set for the second; they are different facts and are never conflated.  Writing an agreement is `POST`/`PUT`/`DELETE /api/v1/contracts/{contractId}/price-rules` and nowhere else, which is why `priceRuleId` is on every agreed row: there is one write path per scope, and this endpoint is not it.  Use `agreedOnly` to narrow the page to the codes this contract actually prices. It combines with every structural filter, which is what makes \"GC codes with no agreed tariff\" — the question that finds a half-loaded tariff sheet — askable at all (#4048).  `coverage` answers the same question without paging: agreed against total per soort prestatie, over the filtered set. It deliberately ignores `agreedOnly`, because that parameter selects on the very thing coverage measures and every group would otherwise report \"n van n\". 
         * @summary List the contract\'s agreed tariff per prestatiecode
         * @param {string} contractId Contract UUID
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Number of items per page
         * @param {string} [search] Search by prestatiecode or description
         * @param {boolean} [agreedOnly] Only return codes this contract has an agreed tariff for
         * @param {ServiceType} [serviceType] Only prestatiecodes of this soort prestatie — CONSULTATION (CO), GROUP (GC) or RESIDENTIAL (VD). 
         * @param {ServiceSetting} [setting] Only prestatiecodes for this behandelsetting. GROUP codes carry none and are never matched by it — a group consult is priced against the cliënt\&#39;s own zorgtraject setting. 
         * @param {ProfessionCode} [professionCode] Only prestatiecodes for this beroep
         * @param {ConsultType} [consultType] Only prestatiecodes for this consulttype
         * @param {number} [durationMinutes] Only prestatiecodes of this duration. The ZPM buckets are 5, 15, 30, 45, 60, 75, 90 and 120, but any positive number is accepted: which durations the catalogue holds is data rather than contract. 
         * @param {string} [validOn] Only prestatiecodes whose validity window covers this date. The catalogue is versioned per year, so this is how a practice narrows a 589-code list to the codes that were declarable in the year the agreement covers.  It says nothing about &#x60;isActive&#x60;: a retired code this contract prices is still listed, which is what keeps a rule that is doing something visible. 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listContractServiceCodeTariffs(contractId: string, page?: number, size?: number, search?: string, agreedOnly?: boolean, serviceType?: ServiceType, setting?: ServiceSetting, professionCode?: ProfessionCode, consultType?: ConsultType, durationMinutes?: number, validOn?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ContractServiceCodeTariffListResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listContractServiceCodeTariffs(contractId, page, size, search, agreedOnly, serviceType, setting, professionCode, consultType, durationMinutes, validOn, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContractsApi.listContractServiceCodeTariffs']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Get all settings (behandelsettings) and their pricing for a contract. Settings define coverage/pricing at the behandelsetting level (e.g., \"Klinisch at 90%\"). 
         * @summary List contract settings
         * @param {string} contractId Contract UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listContractSettings(contractId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<ContractSettingResponse>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listContractSettings(contractId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContractsApi.listContractSettings']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * List contracts in the authenticated user\'s organization with pagination, search, and filtering capabilities.  Multiple contracts per insurer are allowed, each covering different settings. Use the settingId parameter to find contracts covering a specific setting. 
         * @summary List contracts
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Number of items per page
         * @param {string} [sort] Sort specification as \&quot;field,direction\&quot; (e.g., \&quot;effectiveDate,desc\&quot;). Supported fields: effectiveDate, validFrom, validUntil, contractNumber, status, ceilingEuro, createdAt, updatedAt. Any other field falls back to effectiveDate.
         * @param {ContractStatus} [status] Filter by contract status
         * @param {string} [insurerId] Filter by insurer ID - returns every contract that covers this insurer, which includes a contract covering the concern the insurer belongs to. 
         * @param {string} [settingId] Filter by setting ID - returns contracts that cover this setting
         * @param {boolean} [includeExpired] Include expired contracts (default false)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listContracts(page?: number, size?: number, sort?: string, status?: ContractStatus, insurerId?: string, settingId?: string, includeExpired?: boolean, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ContractListResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listContracts(page, size, sort, status, insurerId, settingId, includeExpired, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContractsApi.listContracts']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * The organisation\'s gemeente contracts, most recent first, each with its gemeenten and products. Unpaged: a practice holds a handful. 
         * @summary List gemeente contracts
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listMunicipalContracts(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<MunicipalContractResponse>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listMunicipalContracts(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContractsApi.listMunicipalContracts']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Looks up the effective tariff for a specific insurer and service code. Returns whether there\'s a contract, the NZa tariff, and the effective rate.  The lookup finds the active contract that covers the setting associated with the service code. Since multiple contracts per insurer are allowed (each covering different settings), the response includes the contractId used for pricing. 
         * @summary Look up tariff for billing
         * @param {TariffLookupRequest} tariffLookupRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async lookupTariff(tariffLookupRequest: TariffLookupRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<TariffLookupResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.lookupTariff(tariffLookupRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContractsApi.lookupTariff']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Updates an existing contract. Who it is with cannot be changed — neither `insurerId` nor `concernId` is updatable, because moving a live agreement between labels would silently reprice every consult already declared under it. 
         * @summary Update contract
         * @param {string} contractId Contract UUID
         * @param {UpdateContractRequest} updateContractRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateContract(contractId: string, updateContractRequest: UpdateContractRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ContractResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateContract(contractId, updateContractRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContractsApi.updateContract']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Updates what a price rule charges. Its scope is fixed: moving a rule from one beroep to another is a delete plus an add, so that the uniqueness of the scope it lands on is decided the same way it was on creation. 
         * @summary Update a price rule
         * @param {string} contractId Contract UUID
         * @param {string} priceRuleId Price rule UUID
         * @param {UpdateContractPriceRuleRequest} updateContractPriceRuleRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateContractPriceRule(contractId: string, priceRuleId: string, updateContractPriceRuleRequest: UpdateContractPriceRuleRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ContractPriceRuleResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateContractPriceRule(contractId, priceRuleId, updateContractPriceRuleRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContractsApi.updateContractPriceRule']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Update pricing for an existing setting within a contract.
         * @summary Update contract setting
         * @param {string} contractId Contract UUID
         * @param {string} settingId Contract setting UUID
         * @param {UpdateContractSettingRequest} updateContractSettingRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateContractSetting(contractId: string, settingId: string, updateContractSettingRequest: UpdateContractSettingRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ContractSettingResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateContractSetting(contractId, settingId, updateContractSettingRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContractsApi.updateContractSetting']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Replaces the contract\'s header and the set of gemeenten it covers. An optional field left out is cleared. The financing regime is fixed at creation; the products are written through their own operations. 
         * @summary Replace a gemeente contract\'s header and gemeenten
         * @param {string} contractId Gemeente contract ID
         * @param {UpdateMunicipalContractRequest} updateMunicipalContractRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateMunicipalContract(contractId: string, updateMunicipalContractRequest: UpdateMunicipalContractRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<MunicipalContractResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateMunicipalContract(contractId, updateMunicipalContractRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContractsApi.updateMunicipalContract']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Replaces the categorie, period and amount. A yearly indexation is a new budget, not an edited amount. 
         * @summary Replace a gemeente contract category budget
         * @param {string} contractId Gemeente contract ID
         * @param {string} budgetId Category budget ID
         * @param {MunicipalCategoryBudgetRequest} municipalCategoryBudgetRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateMunicipalContractCategoryBudget(contractId: string, budgetId: string, municipalCategoryBudgetRequest: MunicipalCategoryBudgetRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<MunicipalCategoryBudgetResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateMunicipalContractCategoryBudget(contractId, budgetId, municipalCategoryBudgetRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContractsApi.updateMunicipalContractCategoryBudget']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Replaces the product, its tariffs and its shares as one document, validated as a whole. A new indexation is a new entry in `tarieven`, not an edited amount. 
         * @summary Replace a gemeente contract product
         * @param {string} contractId Gemeente contract ID
         * @param {string} productId Product ID
         * @param {MunicipalContractProductRequest} municipalContractProductRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateMunicipalContractProduct(contractId: string, productId: string, municipalContractProductRequest: MunicipalContractProductRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<MunicipalContractProductResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateMunicipalContractProduct(contractId, productId, municipalContractProductRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ContractsApi.updateMunicipalContractProduct']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * ContractsApi - factory interface
 */
export const ContractsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = ContractsApiFp(configuration)
    return {
        /**
         * Adds one priced scope to the contract. A contract carries at most one rule per scope value — one rule per prestatiecode, one per beroep, and one default — so a second rule for the same scope value is a 409.  `SETTING`-scoped rules are **derived** from the contract\'s behandelsettings and cannot be written here; use the settings endpoints instead. 
         * @summary Add a price rule to a contract
         * @param {string} contractId Contract UUID
         * @param {ContractPriceRuleRequest} contractPriceRuleRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        addContractPriceRule(contractId: string, contractPriceRuleRequest: ContractPriceRuleRequest, options?: RawAxiosRequestConfig): AxiosPromise<ContractPriceRuleResponse> {
            return localVarFp.addContractPriceRule(contractId, contractPriceRuleRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Add a setting (behandelsetting) with pricing to a contract. Each setting can only be added once per contract. 
         * @summary Add setting to contract
         * @param {string} contractId Contract UUID
         * @param {ContractSettingRequest} contractSettingRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        addContractSetting(contractId: string, contractSettingRequest: ContractSettingRequest, options?: RawAxiosRequestConfig): AxiosPromise<ContractSettingResponse> {
            return localVarFp.addContractSetting(contractId, contractSettingRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Adds the maximum budget the contract allows for one productcategorie over one period — the ceiling an aspecifieke toewijzing may request. See `MunicipalCategoryBudgetRequest` for what is refused. 
         * @summary Add a category budget to a gemeente contract
         * @param {string} contractId Gemeente contract ID
         * @param {MunicipalCategoryBudgetRequest} municipalCategoryBudgetRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        addMunicipalContractCategoryBudget(contractId: string, municipalCategoryBudgetRequest: MunicipalCategoryBudgetRequest, options?: RawAxiosRequestConfig): AxiosPromise<MunicipalCategoryBudgetResponse> {
            return localVarFp.addMunicipalContractCategoryBudget(contractId, municipalCategoryBudgetRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Adds one product with its dated tariffs and, for a LUMP_SUM product, its shares. See `MunicipalContractProductRequest` for what is refused. 
         * @summary Add a product to a gemeente contract
         * @param {string} contractId Gemeente contract ID
         * @param {MunicipalContractProductRequest} municipalContractProductRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        addMunicipalContractProduct(contractId: string, municipalContractProductRequest: MunicipalContractProductRequest, options?: RawAxiosRequestConfig): AxiosPromise<MunicipalContractProductResponse> {
            return localVarFp.addMunicipalContractProduct(contractId, municipalContractProductRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Creates a new contract with an insurer. Contracts are created in ACTIVE status.  When creating a new contract for an insurer where an active contract already exists, the existing contract is automatically expired at the day before the new contract\'s effective date. 
         * @summary Create new contract
         * @param {CreateContractRequest} createContractRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createContract(createContractRequest: CreateContractRequest, options?: RawAxiosRequestConfig): AxiosPromise<ContractResponse> {
            return localVarFp.createContract(createContractRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Creates the contract a practice holds with one or more gemeenten for Jeugdwet or Wmo care (#4746): the gemeenten it covers, the products it holds, how each product is declared and at what tariff on which date. Created in ACTIVE status.  Refused with a 400 when the organisation has not enabled the contract\'s financing regime in Organisatiebeheer › Bekostiging. Every validation failure is a 400 whose `errors` array names the offending field.  Nothing is seeded: the practice enters its own contract. A gemeente contract is not an insurer contract — it does not appear under `/api/v1/contracts`, and that path answers 404 for its id. 
         * @summary Create a gemeente contract
         * @param {CreateMunicipalContractRequest} createMunicipalContractRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createMunicipalContract(createMunicipalContractRequest: CreateMunicipalContractRequest, options?: RawAxiosRequestConfig): AxiosPromise<MunicipalContractResponse> {
            return localVarFp.createMunicipalContract(createMunicipalContractRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Deletes a contract. This always succeeds, whatever the contract has behind it — consults, draft invoices, sent invoices, an expired status.  A deleted contract counts nowhere: it prices no consult, appears in no list (`includeExpired` included) and is reported as no contract on the declaratie overview. Openstaande consults that were priced under it resolve to whatever now applies — the organisation\'s own tariff if it set one, else the full NZa maximum.  Lines on invoices that have already been sent are **not** rewritten: each carries the amount and the contract it was priced under. Correcting one of those is the credit-note flow, not this.  Deleting is not reversible. 
         * @summary Delete contract
         * @param {string} contractId Contract UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteContract(contractId: string, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteContract(contractId, options).then((request) => request(axios, basePath));
        },
        /**
         * Removes one priced scope. What the rule priced then falls through to the next rule down the precedence, and a line no rule matches is uncontracted — the full NZa maximum, never a discount nobody agreed (#3822). 
         * @summary Remove a price rule from a contract
         * @param {string} contractId Contract UUID
         * @param {string} priceRuleId Price rule UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteContractPriceRule(contractId: string, priceRuleId: string, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteContractPriceRule(contractId, priceRuleId, options).then((request) => request(axios, basePath));
        },
        /**
         * Remove a setting (behandelsetting) from a contract.
         * @summary Remove setting from contract
         * @param {string} contractId Contract UUID
         * @param {string} settingId Contract setting UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteContractSetting(contractId: string, settingId: string, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteContractSetting(contractId, settingId, options).then((request) => request(axios, basePath));
        },
        /**
         * Soft-deletes the contract, as for an insurer contract (#4555): it covers nothing from then on and is no longer listed. 
         * @summary Delete a gemeente contract
         * @param {string} contractId Gemeente contract ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteMunicipalContract(contractId: string, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteMunicipalContract(contractId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Remove a category budget from a gemeente contract
         * @param {string} contractId Gemeente contract ID
         * @param {string} budgetId Category budget ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteMunicipalContractCategoryBudget(contractId: string, budgetId: string, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteMunicipalContractCategoryBudget(contractId, budgetId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Remove a product from a gemeente contract
         * @param {string} contractId Gemeente contract ID
         * @param {string} productId Product ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteMunicipalContractProduct(contractId: string, productId: string, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteMunicipalContractProduct(contractId, productId, options).then((request) => request(axios, basePath));
        },
        /**
         * Manually expires an active contract. Sets the status to EXPIRED and optionally updates the end date. 
         * @summary Expire contract
         * @param {string} contractId Contract UUID
         * @param {string} [endDate] End date (defaults to today if not provided)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        expireContract(contractId: string, endDate?: string, options?: RawAxiosRequestConfig): AxiosPromise<ContractResponse> {
            return localVarFp.expireContract(contractId, endDate, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieves a single contract by ID. User must be in same organization.
         * @summary Get contract by ID
         * @param {string} contractId Contract UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getContractById(contractId: string, options?: RawAxiosRequestConfig): AxiosPromise<ContractResponse> {
            return localVarFp.getContractById(contractId, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns dashboard summary data including active contract count, expiring contracts, and plafond warnings. 
         * @summary Get contract summary
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getContractSummary(options?: RawAxiosRequestConfig): AxiosPromise<ContractSummaryResponse> {
            return localVarFp.getContractSummary(options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Get a gemeente contract
         * @param {string} contractId Gemeente contract ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getMunicipalContract(contractId: string, options?: RawAxiosRequestConfig): AxiosPromise<MunicipalContractResponse> {
            return localVarFp.getMunicipalContract(contractId, options).then((request) => request(axios, basePath));
        },
        /**
         * Lists the contract\'s price rules (#4025) — one row per priced scope, resolved most-specific-wins: prestatiecode, then beroep, then behandelsetting, then the contract\'s default.  `scope` narrows the answer to one of those. It is not merely a convenience: a contract that prices per prestatiecode carries 222+ rules, and the beroep screen wants at most nine of them.  The answer is **not paged**, and that is bounded by the scopes that have a screen today: a contract carries one default and at most one rule per beroep. The prestatiecode screen (#4030) is the 222-row consumer, and it needs a page cut in the database rather than out of this list — do not reuse this operation for it. 
         * @summary List price rules of a contract
         * @param {string} contractId Contract UUID
         * @param {ContractPriceScope} [scope] Return only the rules of this scope. Omit for every rule of the contract.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listContractPriceRules(contractId: string, scope?: ContractPriceScope, options?: RawAxiosRequestConfig): AxiosPromise<Array<ContractPriceRuleResponse>> {
            return localVarFp.listContractPriceRules(contractId, scope, options).then((request) => request(axios, basePath));
        },
        /**
         * Lists prestatiecodes with the amount this contract agrees for each one, one page at a time.  This is `GET /price-rules?scope=SERVICE_CODE` read at a different grain, and the grain is the point: that one answers with the *rules*, this one with the **codes**. An a.s.r. or Zorg en Zekerheid agreement names 222-225 of them and the practice reconciles the list against the insurer\'s own spreadsheet, so a code with no agreed amount has to be visible beside one agreed at € 0,00 — and 222 rules is past the size at which an unpaged, unsearchable list is usable. `priceRuleId` is `null` for the first and set for the second; they are different facts and are never conflated.  Writing an agreement is `POST`/`PUT`/`DELETE /api/v1/contracts/{contractId}/price-rules` and nowhere else, which is why `priceRuleId` is on every agreed row: there is one write path per scope, and this endpoint is not it.  Use `agreedOnly` to narrow the page to the codes this contract actually prices. It combines with every structural filter, which is what makes \"GC codes with no agreed tariff\" — the question that finds a half-loaded tariff sheet — askable at all (#4048).  `coverage` answers the same question without paging: agreed against total per soort prestatie, over the filtered set. It deliberately ignores `agreedOnly`, because that parameter selects on the very thing coverage measures and every group would otherwise report \"n van n\". 
         * @summary List the contract\'s agreed tariff per prestatiecode
         * @param {string} contractId Contract UUID
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Number of items per page
         * @param {string} [search] Search by prestatiecode or description
         * @param {boolean} [agreedOnly] Only return codes this contract has an agreed tariff for
         * @param {ServiceType} [serviceType] Only prestatiecodes of this soort prestatie — CONSULTATION (CO), GROUP (GC) or RESIDENTIAL (VD). 
         * @param {ServiceSetting} [setting] Only prestatiecodes for this behandelsetting. GROUP codes carry none and are never matched by it — a group consult is priced against the cliënt\&#39;s own zorgtraject setting. 
         * @param {ProfessionCode} [professionCode] Only prestatiecodes for this beroep
         * @param {ConsultType} [consultType] Only prestatiecodes for this consulttype
         * @param {number} [durationMinutes] Only prestatiecodes of this duration. The ZPM buckets are 5, 15, 30, 45, 60, 75, 90 and 120, but any positive number is accepted: which durations the catalogue holds is data rather than contract. 
         * @param {string} [validOn] Only prestatiecodes whose validity window covers this date. The catalogue is versioned per year, so this is how a practice narrows a 589-code list to the codes that were declarable in the year the agreement covers.  It says nothing about &#x60;isActive&#x60;: a retired code this contract prices is still listed, which is what keeps a rule that is doing something visible. 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listContractServiceCodeTariffs(contractId: string, page?: number, size?: number, search?: string, agreedOnly?: boolean, serviceType?: ServiceType, setting?: ServiceSetting, professionCode?: ProfessionCode, consultType?: ConsultType, durationMinutes?: number, validOn?: string, options?: RawAxiosRequestConfig): AxiosPromise<ContractServiceCodeTariffListResponse> {
            return localVarFp.listContractServiceCodeTariffs(contractId, page, size, search, agreedOnly, serviceType, setting, professionCode, consultType, durationMinutes, validOn, options).then((request) => request(axios, basePath));
        },
        /**
         * Get all settings (behandelsettings) and their pricing for a contract. Settings define coverage/pricing at the behandelsetting level (e.g., \"Klinisch at 90%\"). 
         * @summary List contract settings
         * @param {string} contractId Contract UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listContractSettings(contractId: string, options?: RawAxiosRequestConfig): AxiosPromise<Array<ContractSettingResponse>> {
            return localVarFp.listContractSettings(contractId, options).then((request) => request(axios, basePath));
        },
        /**
         * List contracts in the authenticated user\'s organization with pagination, search, and filtering capabilities.  Multiple contracts per insurer are allowed, each covering different settings. Use the settingId parameter to find contracts covering a specific setting. 
         * @summary List contracts
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Number of items per page
         * @param {string} [sort] Sort specification as \&quot;field,direction\&quot; (e.g., \&quot;effectiveDate,desc\&quot;). Supported fields: effectiveDate, validFrom, validUntil, contractNumber, status, ceilingEuro, createdAt, updatedAt. Any other field falls back to effectiveDate.
         * @param {ContractStatus} [status] Filter by contract status
         * @param {string} [insurerId] Filter by insurer ID - returns every contract that covers this insurer, which includes a contract covering the concern the insurer belongs to. 
         * @param {string} [settingId] Filter by setting ID - returns contracts that cover this setting
         * @param {boolean} [includeExpired] Include expired contracts (default false)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listContracts(page?: number, size?: number, sort?: string, status?: ContractStatus, insurerId?: string, settingId?: string, includeExpired?: boolean, options?: RawAxiosRequestConfig): AxiosPromise<ContractListResponse> {
            return localVarFp.listContracts(page, size, sort, status, insurerId, settingId, includeExpired, options).then((request) => request(axios, basePath));
        },
        /**
         * The organisation\'s gemeente contracts, most recent first, each with its gemeenten and products. Unpaged: a practice holds a handful. 
         * @summary List gemeente contracts
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listMunicipalContracts(options?: RawAxiosRequestConfig): AxiosPromise<Array<MunicipalContractResponse>> {
            return localVarFp.listMunicipalContracts(options).then((request) => request(axios, basePath));
        },
        /**
         * Looks up the effective tariff for a specific insurer and service code. Returns whether there\'s a contract, the NZa tariff, and the effective rate.  The lookup finds the active contract that covers the setting associated with the service code. Since multiple contracts per insurer are allowed (each covering different settings), the response includes the contractId used for pricing. 
         * @summary Look up tariff for billing
         * @param {TariffLookupRequest} tariffLookupRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        lookupTariff(tariffLookupRequest: TariffLookupRequest, options?: RawAxiosRequestConfig): AxiosPromise<TariffLookupResponse> {
            return localVarFp.lookupTariff(tariffLookupRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Updates an existing contract. Who it is with cannot be changed — neither `insurerId` nor `concernId` is updatable, because moving a live agreement between labels would silently reprice every consult already declared under it. 
         * @summary Update contract
         * @param {string} contractId Contract UUID
         * @param {UpdateContractRequest} updateContractRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateContract(contractId: string, updateContractRequest: UpdateContractRequest, options?: RawAxiosRequestConfig): AxiosPromise<ContractResponse> {
            return localVarFp.updateContract(contractId, updateContractRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Updates what a price rule charges. Its scope is fixed: moving a rule from one beroep to another is a delete plus an add, so that the uniqueness of the scope it lands on is decided the same way it was on creation. 
         * @summary Update a price rule
         * @param {string} contractId Contract UUID
         * @param {string} priceRuleId Price rule UUID
         * @param {UpdateContractPriceRuleRequest} updateContractPriceRuleRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateContractPriceRule(contractId: string, priceRuleId: string, updateContractPriceRuleRequest: UpdateContractPriceRuleRequest, options?: RawAxiosRequestConfig): AxiosPromise<ContractPriceRuleResponse> {
            return localVarFp.updateContractPriceRule(contractId, priceRuleId, updateContractPriceRuleRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Update pricing for an existing setting within a contract.
         * @summary Update contract setting
         * @param {string} contractId Contract UUID
         * @param {string} settingId Contract setting UUID
         * @param {UpdateContractSettingRequest} updateContractSettingRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateContractSetting(contractId: string, settingId: string, updateContractSettingRequest: UpdateContractSettingRequest, options?: RawAxiosRequestConfig): AxiosPromise<ContractSettingResponse> {
            return localVarFp.updateContractSetting(contractId, settingId, updateContractSettingRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Replaces the contract\'s header and the set of gemeenten it covers. An optional field left out is cleared. The financing regime is fixed at creation; the products are written through their own operations. 
         * @summary Replace a gemeente contract\'s header and gemeenten
         * @param {string} contractId Gemeente contract ID
         * @param {UpdateMunicipalContractRequest} updateMunicipalContractRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateMunicipalContract(contractId: string, updateMunicipalContractRequest: UpdateMunicipalContractRequest, options?: RawAxiosRequestConfig): AxiosPromise<MunicipalContractResponse> {
            return localVarFp.updateMunicipalContract(contractId, updateMunicipalContractRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Replaces the categorie, period and amount. A yearly indexation is a new budget, not an edited amount. 
         * @summary Replace a gemeente contract category budget
         * @param {string} contractId Gemeente contract ID
         * @param {string} budgetId Category budget ID
         * @param {MunicipalCategoryBudgetRequest} municipalCategoryBudgetRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateMunicipalContractCategoryBudget(contractId: string, budgetId: string, municipalCategoryBudgetRequest: MunicipalCategoryBudgetRequest, options?: RawAxiosRequestConfig): AxiosPromise<MunicipalCategoryBudgetResponse> {
            return localVarFp.updateMunicipalContractCategoryBudget(contractId, budgetId, municipalCategoryBudgetRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Replaces the product, its tariffs and its shares as one document, validated as a whole. A new indexation is a new entry in `tarieven`, not an edited amount. 
         * @summary Replace a gemeente contract product
         * @param {string} contractId Gemeente contract ID
         * @param {string} productId Product ID
         * @param {MunicipalContractProductRequest} municipalContractProductRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateMunicipalContractProduct(contractId: string, productId: string, municipalContractProductRequest: MunicipalContractProductRequest, options?: RawAxiosRequestConfig): AxiosPromise<MunicipalContractProductResponse> {
            return localVarFp.updateMunicipalContractProduct(contractId, productId, municipalContractProductRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * ContractsApi - object-oriented interface
 */
export class ContractsApi extends BaseAPI {
    /**
     * Adds one priced scope to the contract. A contract carries at most one rule per scope value — one rule per prestatiecode, one per beroep, and one default — so a second rule for the same scope value is a 409.  `SETTING`-scoped rules are **derived** from the contract\'s behandelsettings and cannot be written here; use the settings endpoints instead. 
     * @summary Add a price rule to a contract
     * @param {string} contractId Contract UUID
     * @param {ContractPriceRuleRequest} contractPriceRuleRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public addContractPriceRule(contractId: string, contractPriceRuleRequest: ContractPriceRuleRequest, options?: RawAxiosRequestConfig) {
        return ContractsApiFp(this.configuration).addContractPriceRule(contractId, contractPriceRuleRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Add a setting (behandelsetting) with pricing to a contract. Each setting can only be added once per contract. 
     * @summary Add setting to contract
     * @param {string} contractId Contract UUID
     * @param {ContractSettingRequest} contractSettingRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public addContractSetting(contractId: string, contractSettingRequest: ContractSettingRequest, options?: RawAxiosRequestConfig) {
        return ContractsApiFp(this.configuration).addContractSetting(contractId, contractSettingRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Adds the maximum budget the contract allows for one productcategorie over one period — the ceiling an aspecifieke toewijzing may request. See `MunicipalCategoryBudgetRequest` for what is refused. 
     * @summary Add a category budget to a gemeente contract
     * @param {string} contractId Gemeente contract ID
     * @param {MunicipalCategoryBudgetRequest} municipalCategoryBudgetRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public addMunicipalContractCategoryBudget(contractId: string, municipalCategoryBudgetRequest: MunicipalCategoryBudgetRequest, options?: RawAxiosRequestConfig) {
        return ContractsApiFp(this.configuration).addMunicipalContractCategoryBudget(contractId, municipalCategoryBudgetRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Adds one product with its dated tariffs and, for a LUMP_SUM product, its shares. See `MunicipalContractProductRequest` for what is refused. 
     * @summary Add a product to a gemeente contract
     * @param {string} contractId Gemeente contract ID
     * @param {MunicipalContractProductRequest} municipalContractProductRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public addMunicipalContractProduct(contractId: string, municipalContractProductRequest: MunicipalContractProductRequest, options?: RawAxiosRequestConfig) {
        return ContractsApiFp(this.configuration).addMunicipalContractProduct(contractId, municipalContractProductRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Creates a new contract with an insurer. Contracts are created in ACTIVE status.  When creating a new contract for an insurer where an active contract already exists, the existing contract is automatically expired at the day before the new contract\'s effective date. 
     * @summary Create new contract
     * @param {CreateContractRequest} createContractRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createContract(createContractRequest: CreateContractRequest, options?: RawAxiosRequestConfig) {
        return ContractsApiFp(this.configuration).createContract(createContractRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Creates the contract a practice holds with one or more gemeenten for Jeugdwet or Wmo care (#4746): the gemeenten it covers, the products it holds, how each product is declared and at what tariff on which date. Created in ACTIVE status.  Refused with a 400 when the organisation has not enabled the contract\'s financing regime in Organisatiebeheer › Bekostiging. Every validation failure is a 400 whose `errors` array names the offending field.  Nothing is seeded: the practice enters its own contract. A gemeente contract is not an insurer contract — it does not appear under `/api/v1/contracts`, and that path answers 404 for its id. 
     * @summary Create a gemeente contract
     * @param {CreateMunicipalContractRequest} createMunicipalContractRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createMunicipalContract(createMunicipalContractRequest: CreateMunicipalContractRequest, options?: RawAxiosRequestConfig) {
        return ContractsApiFp(this.configuration).createMunicipalContract(createMunicipalContractRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Deletes a contract. This always succeeds, whatever the contract has behind it — consults, draft invoices, sent invoices, an expired status.  A deleted contract counts nowhere: it prices no consult, appears in no list (`includeExpired` included) and is reported as no contract on the declaratie overview. Openstaande consults that were priced under it resolve to whatever now applies — the organisation\'s own tariff if it set one, else the full NZa maximum.  Lines on invoices that have already been sent are **not** rewritten: each carries the amount and the contract it was priced under. Correcting one of those is the credit-note flow, not this.  Deleting is not reversible. 
     * @summary Delete contract
     * @param {string} contractId Contract UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteContract(contractId: string, options?: RawAxiosRequestConfig) {
        return ContractsApiFp(this.configuration).deleteContract(contractId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Removes one priced scope. What the rule priced then falls through to the next rule down the precedence, and a line no rule matches is uncontracted — the full NZa maximum, never a discount nobody agreed (#3822). 
     * @summary Remove a price rule from a contract
     * @param {string} contractId Contract UUID
     * @param {string} priceRuleId Price rule UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteContractPriceRule(contractId: string, priceRuleId: string, options?: RawAxiosRequestConfig) {
        return ContractsApiFp(this.configuration).deleteContractPriceRule(contractId, priceRuleId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Remove a setting (behandelsetting) from a contract.
     * @summary Remove setting from contract
     * @param {string} contractId Contract UUID
     * @param {string} settingId Contract setting UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteContractSetting(contractId: string, settingId: string, options?: RawAxiosRequestConfig) {
        return ContractsApiFp(this.configuration).deleteContractSetting(contractId, settingId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Soft-deletes the contract, as for an insurer contract (#4555): it covers nothing from then on and is no longer listed. 
     * @summary Delete a gemeente contract
     * @param {string} contractId Gemeente contract ID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteMunicipalContract(contractId: string, options?: RawAxiosRequestConfig) {
        return ContractsApiFp(this.configuration).deleteMunicipalContract(contractId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Remove a category budget from a gemeente contract
     * @param {string} contractId Gemeente contract ID
     * @param {string} budgetId Category budget ID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteMunicipalContractCategoryBudget(contractId: string, budgetId: string, options?: RawAxiosRequestConfig) {
        return ContractsApiFp(this.configuration).deleteMunicipalContractCategoryBudget(contractId, budgetId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Remove a product from a gemeente contract
     * @param {string} contractId Gemeente contract ID
     * @param {string} productId Product ID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteMunicipalContractProduct(contractId: string, productId: string, options?: RawAxiosRequestConfig) {
        return ContractsApiFp(this.configuration).deleteMunicipalContractProduct(contractId, productId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Manually expires an active contract. Sets the status to EXPIRED and optionally updates the end date. 
     * @summary Expire contract
     * @param {string} contractId Contract UUID
     * @param {string} [endDate] End date (defaults to today if not provided)
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public expireContract(contractId: string, endDate?: string, options?: RawAxiosRequestConfig) {
        return ContractsApiFp(this.configuration).expireContract(contractId, endDate, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieves a single contract by ID. User must be in same organization.
     * @summary Get contract by ID
     * @param {string} contractId Contract UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getContractById(contractId: string, options?: RawAxiosRequestConfig) {
        return ContractsApiFp(this.configuration).getContractById(contractId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns dashboard summary data including active contract count, expiring contracts, and plafond warnings. 
     * @summary Get contract summary
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getContractSummary(options?: RawAxiosRequestConfig) {
        return ContractsApiFp(this.configuration).getContractSummary(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Get a gemeente contract
     * @param {string} contractId Gemeente contract ID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getMunicipalContract(contractId: string, options?: RawAxiosRequestConfig) {
        return ContractsApiFp(this.configuration).getMunicipalContract(contractId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Lists the contract\'s price rules (#4025) — one row per priced scope, resolved most-specific-wins: prestatiecode, then beroep, then behandelsetting, then the contract\'s default.  `scope` narrows the answer to one of those. It is not merely a convenience: a contract that prices per prestatiecode carries 222+ rules, and the beroep screen wants at most nine of them.  The answer is **not paged**, and that is bounded by the scopes that have a screen today: a contract carries one default and at most one rule per beroep. The prestatiecode screen (#4030) is the 222-row consumer, and it needs a page cut in the database rather than out of this list — do not reuse this operation for it. 
     * @summary List price rules of a contract
     * @param {string} contractId Contract UUID
     * @param {ContractPriceScope} [scope] Return only the rules of this scope. Omit for every rule of the contract.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listContractPriceRules(contractId: string, scope?: ContractPriceScope, options?: RawAxiosRequestConfig) {
        return ContractsApiFp(this.configuration).listContractPriceRules(contractId, scope, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Lists prestatiecodes with the amount this contract agrees for each one, one page at a time.  This is `GET /price-rules?scope=SERVICE_CODE` read at a different grain, and the grain is the point: that one answers with the *rules*, this one with the **codes**. An a.s.r. or Zorg en Zekerheid agreement names 222-225 of them and the practice reconciles the list against the insurer\'s own spreadsheet, so a code with no agreed amount has to be visible beside one agreed at € 0,00 — and 222 rules is past the size at which an unpaged, unsearchable list is usable. `priceRuleId` is `null` for the first and set for the second; they are different facts and are never conflated.  Writing an agreement is `POST`/`PUT`/`DELETE /api/v1/contracts/{contractId}/price-rules` and nowhere else, which is why `priceRuleId` is on every agreed row: there is one write path per scope, and this endpoint is not it.  Use `agreedOnly` to narrow the page to the codes this contract actually prices. It combines with every structural filter, which is what makes \"GC codes with no agreed tariff\" — the question that finds a half-loaded tariff sheet — askable at all (#4048).  `coverage` answers the same question without paging: agreed against total per soort prestatie, over the filtered set. It deliberately ignores `agreedOnly`, because that parameter selects on the very thing coverage measures and every group would otherwise report \"n van n\". 
     * @summary List the contract\'s agreed tariff per prestatiecode
     * @param {string} contractId Contract UUID
     * @param {number} [page] Page number (0-indexed)
     * @param {number} [size] Number of items per page
     * @param {string} [search] Search by prestatiecode or description
     * @param {boolean} [agreedOnly] Only return codes this contract has an agreed tariff for
     * @param {ServiceType} [serviceType] Only prestatiecodes of this soort prestatie — CONSULTATION (CO), GROUP (GC) or RESIDENTIAL (VD). 
     * @param {ServiceSetting} [setting] Only prestatiecodes for this behandelsetting. GROUP codes carry none and are never matched by it — a group consult is priced against the cliënt\&#39;s own zorgtraject setting. 
     * @param {ProfessionCode} [professionCode] Only prestatiecodes for this beroep
     * @param {ConsultType} [consultType] Only prestatiecodes for this consulttype
     * @param {number} [durationMinutes] Only prestatiecodes of this duration. The ZPM buckets are 5, 15, 30, 45, 60, 75, 90 and 120, but any positive number is accepted: which durations the catalogue holds is data rather than contract. 
     * @param {string} [validOn] Only prestatiecodes whose validity window covers this date. The catalogue is versioned per year, so this is how a practice narrows a 589-code list to the codes that were declarable in the year the agreement covers.  It says nothing about &#x60;isActive&#x60;: a retired code this contract prices is still listed, which is what keeps a rule that is doing something visible. 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listContractServiceCodeTariffs(contractId: string, page?: number, size?: number, search?: string, agreedOnly?: boolean, serviceType?: ServiceType, setting?: ServiceSetting, professionCode?: ProfessionCode, consultType?: ConsultType, durationMinutes?: number, validOn?: string, options?: RawAxiosRequestConfig) {
        return ContractsApiFp(this.configuration).listContractServiceCodeTariffs(contractId, page, size, search, agreedOnly, serviceType, setting, professionCode, consultType, durationMinutes, validOn, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Get all settings (behandelsettings) and their pricing for a contract. Settings define coverage/pricing at the behandelsetting level (e.g., \"Klinisch at 90%\"). 
     * @summary List contract settings
     * @param {string} contractId Contract UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listContractSettings(contractId: string, options?: RawAxiosRequestConfig) {
        return ContractsApiFp(this.configuration).listContractSettings(contractId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * List contracts in the authenticated user\'s organization with pagination, search, and filtering capabilities.  Multiple contracts per insurer are allowed, each covering different settings. Use the settingId parameter to find contracts covering a specific setting. 
     * @summary List contracts
     * @param {number} [page] Page number (0-indexed)
     * @param {number} [size] Number of items per page
     * @param {string} [sort] Sort specification as \&quot;field,direction\&quot; (e.g., \&quot;effectiveDate,desc\&quot;). Supported fields: effectiveDate, validFrom, validUntil, contractNumber, status, ceilingEuro, createdAt, updatedAt. Any other field falls back to effectiveDate.
     * @param {ContractStatus} [status] Filter by contract status
     * @param {string} [insurerId] Filter by insurer ID - returns every contract that covers this insurer, which includes a contract covering the concern the insurer belongs to. 
     * @param {string} [settingId] Filter by setting ID - returns contracts that cover this setting
     * @param {boolean} [includeExpired] Include expired contracts (default false)
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listContracts(page?: number, size?: number, sort?: string, status?: ContractStatus, insurerId?: string, settingId?: string, includeExpired?: boolean, options?: RawAxiosRequestConfig) {
        return ContractsApiFp(this.configuration).listContracts(page, size, sort, status, insurerId, settingId, includeExpired, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * The organisation\'s gemeente contracts, most recent first, each with its gemeenten and products. Unpaged: a practice holds a handful. 
     * @summary List gemeente contracts
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listMunicipalContracts(options?: RawAxiosRequestConfig) {
        return ContractsApiFp(this.configuration).listMunicipalContracts(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Looks up the effective tariff for a specific insurer and service code. Returns whether there\'s a contract, the NZa tariff, and the effective rate.  The lookup finds the active contract that covers the setting associated with the service code. Since multiple contracts per insurer are allowed (each covering different settings), the response includes the contractId used for pricing. 
     * @summary Look up tariff for billing
     * @param {TariffLookupRequest} tariffLookupRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public lookupTariff(tariffLookupRequest: TariffLookupRequest, options?: RawAxiosRequestConfig) {
        return ContractsApiFp(this.configuration).lookupTariff(tariffLookupRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Updates an existing contract. Who it is with cannot be changed — neither `insurerId` nor `concernId` is updatable, because moving a live agreement between labels would silently reprice every consult already declared under it. 
     * @summary Update contract
     * @param {string} contractId Contract UUID
     * @param {UpdateContractRequest} updateContractRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateContract(contractId: string, updateContractRequest: UpdateContractRequest, options?: RawAxiosRequestConfig) {
        return ContractsApiFp(this.configuration).updateContract(contractId, updateContractRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Updates what a price rule charges. Its scope is fixed: moving a rule from one beroep to another is a delete plus an add, so that the uniqueness of the scope it lands on is decided the same way it was on creation. 
     * @summary Update a price rule
     * @param {string} contractId Contract UUID
     * @param {string} priceRuleId Price rule UUID
     * @param {UpdateContractPriceRuleRequest} updateContractPriceRuleRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateContractPriceRule(contractId: string, priceRuleId: string, updateContractPriceRuleRequest: UpdateContractPriceRuleRequest, options?: RawAxiosRequestConfig) {
        return ContractsApiFp(this.configuration).updateContractPriceRule(contractId, priceRuleId, updateContractPriceRuleRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Update pricing for an existing setting within a contract.
     * @summary Update contract setting
     * @param {string} contractId Contract UUID
     * @param {string} settingId Contract setting UUID
     * @param {UpdateContractSettingRequest} updateContractSettingRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateContractSetting(contractId: string, settingId: string, updateContractSettingRequest: UpdateContractSettingRequest, options?: RawAxiosRequestConfig) {
        return ContractsApiFp(this.configuration).updateContractSetting(contractId, settingId, updateContractSettingRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Replaces the contract\'s header and the set of gemeenten it covers. An optional field left out is cleared. The financing regime is fixed at creation; the products are written through their own operations. 
     * @summary Replace a gemeente contract\'s header and gemeenten
     * @param {string} contractId Gemeente contract ID
     * @param {UpdateMunicipalContractRequest} updateMunicipalContractRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateMunicipalContract(contractId: string, updateMunicipalContractRequest: UpdateMunicipalContractRequest, options?: RawAxiosRequestConfig) {
        return ContractsApiFp(this.configuration).updateMunicipalContract(contractId, updateMunicipalContractRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Replaces the categorie, period and amount. A yearly indexation is a new budget, not an edited amount. 
     * @summary Replace a gemeente contract category budget
     * @param {string} contractId Gemeente contract ID
     * @param {string} budgetId Category budget ID
     * @param {MunicipalCategoryBudgetRequest} municipalCategoryBudgetRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateMunicipalContractCategoryBudget(contractId: string, budgetId: string, municipalCategoryBudgetRequest: MunicipalCategoryBudgetRequest, options?: RawAxiosRequestConfig) {
        return ContractsApiFp(this.configuration).updateMunicipalContractCategoryBudget(contractId, budgetId, municipalCategoryBudgetRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Replaces the product, its tariffs and its shares as one document, validated as a whole. A new indexation is a new entry in `tarieven`, not an edited amount. 
     * @summary Replace a gemeente contract product
     * @param {string} contractId Gemeente contract ID
     * @param {string} productId Product ID
     * @param {MunicipalContractProductRequest} municipalContractProductRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateMunicipalContractProduct(contractId: string, productId: string, municipalContractProductRequest: MunicipalContractProductRequest, options?: RawAxiosRequestConfig) {
        return ContractsApiFp(this.configuration).updateMunicipalContractProduct(contractId, productId, municipalContractProductRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * ReferenceDataApi - axios parameter creator
 */
export const ReferenceDataApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * The gemeenten (CBS gemeentecode and name) that existed on `date`, ordered by name — what a gemeente contract\'s `gemeentecodes` are picked from (#4793), and what names and confirms a cliënt\'s verantwoordelijke gemeente. A gemeente a herindeling had ended by `date`, or one not yet created, is left out. Reference data, read-only; `contracts.read` or `clients.read`. 
         * @summary List gemeenten
         * @param {string} [date] The date the gemeenten must exist on; today (Amsterdam) when omitted
         * @param {string} [search] Case-insensitive match anywhere in the gemeentenaam
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listGemeenten: async (date?: string, search?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/gemeenten`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (date !== undefined) {
                localVarQueryParameter['date'] = (date as any instanceof Date) ?
                    (date as any).toISOString().substring(0,10) :
                    date;
            }

            if (search !== undefined) {
                localVarQueryParameter['search'] = search;
            }

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Lists the insurer concerns (koepels) the calling organization may contract with — the groupings one agreement can cover, each with the labels it holds.  Scoped by the same VECOZO-environment rule as `/api/v1/insurers` (ADR-031), and derived from it: a concern is exactly the set of its labels, so a concern whose labels all belong to the other environment is not listed. 
         * @summary List insurer concerns
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listInsurerConcerns: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/insurer-concerns`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Lists the health insurers (verzekeraars) the calling organization may select.  The catalogue is reference data, but which half of it an organization sees depends on the VECOZO environment this deployment talks to (ADR-044): production serves the real zorgverzekeraars and never the fictitious test payer (UZOVI 1000), staging (acceptance) serves only that test payer. 
         * @summary List insurers
         * @param {string} [search] Search by name or UZOVI code
         * @param {boolean} [activeOnly] Only show active insurers (default true)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listInsurers: async (search?: string, activeOnly?: boolean, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/insurers`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (search !== undefined) {
                localVarQueryParameter['search'] = search;
            }

            if (activeOnly !== undefined) {
                localVarQueryParameter['activeOnly'] = activeOnly;
            }

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * The jeugdregio\'s (CBS *Gebieden in Nederland*) that existed on `date`, ordered by name, each with the gemeenten it held that day — what \"Regio kiezen\" on a gemeente contract fills the contract\'s `gemeentecodes` with (#4806). A suggestion, not a rule: the CBS jeugdregio is not always the inkoopregio, so the practice edits the set and the contract\'s gemeenten remain what it covers. Reference data, read-only. 
         * @summary List jeugdregio\'s
         * @param {string} [date] The date the jeugdregio\&#39;s and their gemeenten must exist on; today (Amsterdam) when omitted
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listJeugdregios: async (date?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/jeugdregios`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (date !== undefined) {
                localVarQueryParameter['date'] = (date as any instanceof Date) ?
                    (date as any).toISOString().substring(0,10) :
                    date;
            }

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Lists all available service codes (prestatie codes) with their NZa tariffs. This is reference data, not organization-specific. 
         * @summary List service codes
         * @param {string} [search] Search by code or description
         * @param {ActivityType} [activityType] Filter by activity type
         * @param {boolean} [activeOnly] Only show active service codes (default true)
         * @param {number} [year] Year for NZa tariff (defaults to current year)
         * @param {boolean} [clientInvoiced] When true, only the codes that are invoiced to the cliënt and may be chosen as an afspraaktype\&#39;s fixed prestatiecode; when false, only the others. Omitted lists both.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listServiceCodes: async (search?: string, activityType?: ActivityType, activeOnly?: boolean, year?: number, clientInvoiced?: boolean, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/service-codes`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (search !== undefined) {
                localVarQueryParameter['search'] = search;
            }

            if (activityType !== undefined) {
                localVarQueryParameter['activityType'] = activityType;
            }

            if (activeOnly !== undefined) {
                localVarQueryParameter['activeOnly'] = activeOnly;
            }

            if (year !== undefined) {
                localVarQueryParameter['year'] = year;
            }

            if (clientInvoiced !== undefined) {
                localVarQueryParameter['clientInvoiced'] = clientInvoiced;
            }

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * ReferenceDataApi - functional programming interface
 */
export const ReferenceDataApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = ReferenceDataApiAxiosParamCreator(configuration)
    return {
        /**
         * The gemeenten (CBS gemeentecode and name) that existed on `date`, ordered by name — what a gemeente contract\'s `gemeentecodes` are picked from (#4793), and what names and confirms a cliënt\'s verantwoordelijke gemeente. A gemeente a herindeling had ended by `date`, or one not yet created, is left out. Reference data, read-only; `contracts.read` or `clients.read`. 
         * @summary List gemeenten
         * @param {string} [date] The date the gemeenten must exist on; today (Amsterdam) when omitted
         * @param {string} [search] Case-insensitive match anywhere in the gemeentenaam
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listGemeenten(date?: string, search?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<GemeenteResponse>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listGemeenten(date, search, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ReferenceDataApi.listGemeenten']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Lists the insurer concerns (koepels) the calling organization may contract with — the groupings one agreement can cover, each with the labels it holds.  Scoped by the same VECOZO-environment rule as `/api/v1/insurers` (ADR-031), and derived from it: a concern is exactly the set of its labels, so a concern whose labels all belong to the other environment is not listed. 
         * @summary List insurer concerns
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listInsurerConcerns(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<InsurerConcernResponse>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listInsurerConcerns(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ReferenceDataApi.listInsurerConcerns']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Lists the health insurers (verzekeraars) the calling organization may select.  The catalogue is reference data, but which half of it an organization sees depends on the VECOZO environment this deployment talks to (ADR-044): production serves the real zorgverzekeraars and never the fictitious test payer (UZOVI 1000), staging (acceptance) serves only that test payer. 
         * @summary List insurers
         * @param {string} [search] Search by name or UZOVI code
         * @param {boolean} [activeOnly] Only show active insurers (default true)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listInsurers(search?: string, activeOnly?: boolean, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<InsurerResponse>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listInsurers(search, activeOnly, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ReferenceDataApi.listInsurers']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * The jeugdregio\'s (CBS *Gebieden in Nederland*) that existed on `date`, ordered by name, each with the gemeenten it held that day — what \"Regio kiezen\" on a gemeente contract fills the contract\'s `gemeentecodes` with (#4806). A suggestion, not a rule: the CBS jeugdregio is not always the inkoopregio, so the practice edits the set and the contract\'s gemeenten remain what it covers. Reference data, read-only. 
         * @summary List jeugdregio\'s
         * @param {string} [date] The date the jeugdregio\&#39;s and their gemeenten must exist on; today (Amsterdam) when omitted
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listJeugdregios(date?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<JeugdregioResponse>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listJeugdregios(date, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ReferenceDataApi.listJeugdregios']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Lists all available service codes (prestatie codes) with their NZa tariffs. This is reference data, not organization-specific. 
         * @summary List service codes
         * @param {string} [search] Search by code or description
         * @param {ActivityType} [activityType] Filter by activity type
         * @param {boolean} [activeOnly] Only show active service codes (default true)
         * @param {number} [year] Year for NZa tariff (defaults to current year)
         * @param {boolean} [clientInvoiced] When true, only the codes that are invoiced to the cliënt and may be chosen as an afspraaktype\&#39;s fixed prestatiecode; when false, only the others. Omitted lists both.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listServiceCodes(search?: string, activityType?: ActivityType, activeOnly?: boolean, year?: number, clientInvoiced?: boolean, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<ServiceCodeResponse>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listServiceCodes(search, activityType, activeOnly, year, clientInvoiced, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ReferenceDataApi.listServiceCodes']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * ReferenceDataApi - factory interface
 */
export const ReferenceDataApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = ReferenceDataApiFp(configuration)
    return {
        /**
         * The gemeenten (CBS gemeentecode and name) that existed on `date`, ordered by name — what a gemeente contract\'s `gemeentecodes` are picked from (#4793), and what names and confirms a cliënt\'s verantwoordelijke gemeente. A gemeente a herindeling had ended by `date`, or one not yet created, is left out. Reference data, read-only; `contracts.read` or `clients.read`. 
         * @summary List gemeenten
         * @param {string} [date] The date the gemeenten must exist on; today (Amsterdam) when omitted
         * @param {string} [search] Case-insensitive match anywhere in the gemeentenaam
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listGemeenten(date?: string, search?: string, options?: RawAxiosRequestConfig): AxiosPromise<Array<GemeenteResponse>> {
            return localVarFp.listGemeenten(date, search, options).then((request) => request(axios, basePath));
        },
        /**
         * Lists the insurer concerns (koepels) the calling organization may contract with — the groupings one agreement can cover, each with the labels it holds.  Scoped by the same VECOZO-environment rule as `/api/v1/insurers` (ADR-031), and derived from it: a concern is exactly the set of its labels, so a concern whose labels all belong to the other environment is not listed. 
         * @summary List insurer concerns
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listInsurerConcerns(options?: RawAxiosRequestConfig): AxiosPromise<Array<InsurerConcernResponse>> {
            return localVarFp.listInsurerConcerns(options).then((request) => request(axios, basePath));
        },
        /**
         * Lists the health insurers (verzekeraars) the calling organization may select.  The catalogue is reference data, but which half of it an organization sees depends on the VECOZO environment this deployment talks to (ADR-044): production serves the real zorgverzekeraars and never the fictitious test payer (UZOVI 1000), staging (acceptance) serves only that test payer. 
         * @summary List insurers
         * @param {string} [search] Search by name or UZOVI code
         * @param {boolean} [activeOnly] Only show active insurers (default true)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listInsurers(search?: string, activeOnly?: boolean, options?: RawAxiosRequestConfig): AxiosPromise<Array<InsurerResponse>> {
            return localVarFp.listInsurers(search, activeOnly, options).then((request) => request(axios, basePath));
        },
        /**
         * The jeugdregio\'s (CBS *Gebieden in Nederland*) that existed on `date`, ordered by name, each with the gemeenten it held that day — what \"Regio kiezen\" on a gemeente contract fills the contract\'s `gemeentecodes` with (#4806). A suggestion, not a rule: the CBS jeugdregio is not always the inkoopregio, so the practice edits the set and the contract\'s gemeenten remain what it covers. Reference data, read-only. 
         * @summary List jeugdregio\'s
         * @param {string} [date] The date the jeugdregio\&#39;s and their gemeenten must exist on; today (Amsterdam) when omitted
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listJeugdregios(date?: string, options?: RawAxiosRequestConfig): AxiosPromise<Array<JeugdregioResponse>> {
            return localVarFp.listJeugdregios(date, options).then((request) => request(axios, basePath));
        },
        /**
         * Lists all available service codes (prestatie codes) with their NZa tariffs. This is reference data, not organization-specific. 
         * @summary List service codes
         * @param {string} [search] Search by code or description
         * @param {ActivityType} [activityType] Filter by activity type
         * @param {boolean} [activeOnly] Only show active service codes (default true)
         * @param {number} [year] Year for NZa tariff (defaults to current year)
         * @param {boolean} [clientInvoiced] When true, only the codes that are invoiced to the cliënt and may be chosen as an afspraaktype\&#39;s fixed prestatiecode; when false, only the others. Omitted lists both.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listServiceCodes(search?: string, activityType?: ActivityType, activeOnly?: boolean, year?: number, clientInvoiced?: boolean, options?: RawAxiosRequestConfig): AxiosPromise<Array<ServiceCodeResponse>> {
            return localVarFp.listServiceCodes(search, activityType, activeOnly, year, clientInvoiced, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * ReferenceDataApi - object-oriented interface
 */
export class ReferenceDataApi extends BaseAPI {
    /**
     * The gemeenten (CBS gemeentecode and name) that existed on `date`, ordered by name — what a gemeente contract\'s `gemeentecodes` are picked from (#4793), and what names and confirms a cliënt\'s verantwoordelijke gemeente. A gemeente a herindeling had ended by `date`, or one not yet created, is left out. Reference data, read-only; `contracts.read` or `clients.read`. 
     * @summary List gemeenten
     * @param {string} [date] The date the gemeenten must exist on; today (Amsterdam) when omitted
     * @param {string} [search] Case-insensitive match anywhere in the gemeentenaam
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listGemeenten(date?: string, search?: string, options?: RawAxiosRequestConfig) {
        return ReferenceDataApiFp(this.configuration).listGemeenten(date, search, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Lists the insurer concerns (koepels) the calling organization may contract with — the groupings one agreement can cover, each with the labels it holds.  Scoped by the same VECOZO-environment rule as `/api/v1/insurers` (ADR-031), and derived from it: a concern is exactly the set of its labels, so a concern whose labels all belong to the other environment is not listed. 
     * @summary List insurer concerns
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listInsurerConcerns(options?: RawAxiosRequestConfig) {
        return ReferenceDataApiFp(this.configuration).listInsurerConcerns(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Lists the health insurers (verzekeraars) the calling organization may select.  The catalogue is reference data, but which half of it an organization sees depends on the VECOZO environment this deployment talks to (ADR-044): production serves the real zorgverzekeraars and never the fictitious test payer (UZOVI 1000), staging (acceptance) serves only that test payer. 
     * @summary List insurers
     * @param {string} [search] Search by name or UZOVI code
     * @param {boolean} [activeOnly] Only show active insurers (default true)
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listInsurers(search?: string, activeOnly?: boolean, options?: RawAxiosRequestConfig) {
        return ReferenceDataApiFp(this.configuration).listInsurers(search, activeOnly, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * The jeugdregio\'s (CBS *Gebieden in Nederland*) that existed on `date`, ordered by name, each with the gemeenten it held that day — what \"Regio kiezen\" on a gemeente contract fills the contract\'s `gemeentecodes` with (#4806). A suggestion, not a rule: the CBS jeugdregio is not always the inkoopregio, so the practice edits the set and the contract\'s gemeenten remain what it covers. Reference data, read-only. 
     * @summary List jeugdregio\'s
     * @param {string} [date] The date the jeugdregio\&#39;s and their gemeenten must exist on; today (Amsterdam) when omitted
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listJeugdregios(date?: string, options?: RawAxiosRequestConfig) {
        return ReferenceDataApiFp(this.configuration).listJeugdregios(date, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Lists all available service codes (prestatie codes) with their NZa tariffs. This is reference data, not organization-specific. 
     * @summary List service codes
     * @param {string} [search] Search by code or description
     * @param {ActivityType} [activityType] Filter by activity type
     * @param {boolean} [activeOnly] Only show active service codes (default true)
     * @param {number} [year] Year for NZa tariff (defaults to current year)
     * @param {boolean} [clientInvoiced] When true, only the codes that are invoiced to the cliënt and may be chosen as an afspraaktype\&#39;s fixed prestatiecode; when false, only the others. Omitted lists both.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listServiceCodes(search?: string, activityType?: ActivityType, activeOnly?: boolean, year?: number, clientInvoiced?: boolean, options?: RawAxiosRequestConfig) {
        return ReferenceDataApiFp(this.configuration).listServiceCodes(search, activityType, activeOnly, year, clientInvoiced, options).then((request) => request(this.axios, this.basePath));
    }
}



